import { tradutorDe, type Tradutor } from '@vestigo/core/i18n-da-tela';
/**
 * O cliente HTTP da área do aluno.
 *
 * Enxuto de propósito: é uma cópia reduzida do cliente do painel, não um import
 * dele. Compartilhar o módulo traria junto o header CSRF do painel, o
 * tratamento de `role` e o redirecionamento para `/entrar` — e, principalmente,
 * faria o bundle do aluno conhecer rotas de `/api/*` que ele nunca deve chamar.
 */

/* `import type`, e do subcaminho: o tipo é apagado na compilação, então o
 * bundle do aluno não ganha um byte de `@vestigo/core` por causa dele — e
 * mesmo assim a tela e o servidor não podem discordar sobre o formato de um
 * marco, que é o que uma segunda declaração garantiria. */
import type { MarcoDaTrilha } from '@vestigo/core/area-trilha';
/* O mesmo argumento, para a marca: o formato que a rota manda por `logo`,
   `favicon` e `compartilhamento` é o do servidor, e uma segunda declaração aqui
   é exatamente o que deixou o cabeçalho servir `[object Object]`. */
import type { ImagemDaMarca } from '@vestigo/core/area-marca';
import type { ProporcaoDeCapa, TamanhoDeCapa } from '@vestigo/core/area-largura-da-capa';
/* O tipo vem do core desde 24/09/2026 (`area-frase-da-tranca-de-ordem`), e
   `lib/tranca.ts` reexporta-o — é de lá que as telas desta pasta o leem, e é
   isso que mantém as cercas a apontar para um sítio só. */
import type { TrancaDeOrdem } from './tranca.js';
/* A DATA de estreia (24/09/2026). O tipo e a conta do dia local moram em
   `lancamento.ts`, e não aqui, porque a contagem é desenho — e porque o
   gotejamento da AULA (`fraseDoCadeado`, abaixo) passou a usar a mesma conta. */
import { diasAte, type Lancamento } from './lancamento.js';
/* O mesmo argumento, para o placar: `import type` do subcaminho, apagado na
   compilação. O VALOR (`PERIODOS_DO_PLACAR`, `PISO_DA_TURMA`) é importado só
   dentro de `Comunidade.tsx`, que já é um `lazy()` próprio — o pedaço de
   entrada não ganha um byte por causa do placar. */
import type { DistribuicaoPorNivel, PeriodoDoPlacar } from '@vestigo/core/area-placar';
/* `H3`: só o TIPO do nível — zero bytes, e nenhuma escada neste pacote. */
import type { NivelDaPessoa } from '@vestigo/core/area-gamificacao';
/* O mesmo argumento, para o postal: `import type` do subcaminho, apagado na
   compilacao. O que NAO se apaga e o mapa de desenhos, e ele e importado como
   VALOR so dentro de `Comunidade.tsx`, que ja e um `lazy()` proprio — o pedaco
   de entrada nao ganha um byte por causa do trofeu. */
import type {
  ConquistaDoAluno,
  GeneroDaConquista,
  PayloadDaConquista,
} from '@vestigo/core/area-conquista';
/* A espinha (`D5`) é montada no CORE — piso, visibilidade herdada e ordem — e o
   fio só carrega a forma que ela devolveu. Reescrever o tipo aqui daria duas
   ideias sobre o que é uma aula da espinha, e a que ficasse diferente seria a
   da tela. */
import type { GrupoDaEspinha } from '@vestigo/core/area-comunidade';

export type {
  MarcoDaTrilha,
  TamanhoDeCapa,
  ConquistaDoAluno,
  GeneroDaConquista,
  PayloadDaConquista,
  GrupoDaEspinha,
};

const BASE = '/aluno/api';

/** O cookie de CSRF do aluno é legível por JS de propósito (double submit). */
function csrf(): string {
  const m = document.cookie.match(/(?:^|;\s*)vestigo_aluno_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]!) : '';
}

/**
 * O cookie de CSRF do PAINEL, quando o navegador o tem — ver o `pedir` abaixo.
 *
 * `''` para toda a base de alunos, e é o caso normal. Ele só existe no host do
 * painel e só para quem tem sessão de operador viva: `vestigo_session` e
 * `vestigo_aluno` têm nomes diferentes e **coexistem** (medido em produção,
 * 07/09/2026, três vezes) — *o que derruba um aluno é plantar outro aluno.*
 */
function csrfDoPainel(): string {
  const m = document.cookie.match(/(?:^|;\s*)vestigo_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]!) : '';
}

export class ErroDaApi extends Error {
  constructor(
    readonly status: number,
    readonly codigo: string,
    message: string,
    /**
     * O que o servidor mandou junto.
     *
     * Existe porque uma recusa nem sempre é um beco: `curso_nao_comprado` vem
     * com o endereço da página de venda, e `aula_bloqueada` com a data em que
     * abre. Sem isto o app mostrava a frase seca e engolia a saída — o servidor
     * oferecia uma porta e a tela desenhava uma parede.
     */
    readonly detalhes?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ErroDaApi';
  }

  /** A sessão acabou (ou nunca existiu). A tela leva ao login do aluno. */
  get precisaEntrar(): boolean {
    return this.status === 401;
  }
}

/**
 * A frase que a pessoa lê — e, num `validation_error`, o MOTIVO em vez do rótulo.
 *
 * ## O caso que escreveu isto (25/09/2026, produção)
 *
 * O handler de `ZodError` do servidor responde sempre a mesma frase —
 * `«Dados inválidos.»` — e guarda o que de facto falhou num `details` que
 * ninguém desenhava. Um aluno viu essa frase cinco vezes seguidas, ao lado de um
 * e-mail certo e de um código certo; o que falhava era a senha, curta demais.
 *
 * «Dados inválidos» aponta para o formulário inteiro. Quem o lê confere o que
 * está à vista — e o campo culpado era o único cujo conteúdo a tela esconde
 * (`type="password"`). Ele concluiu que o **código** estava partido e pediu
 * outro. Cinco vezes.
 *
 * ## Por que a PRIMEIRA questão, e não todas
 *
 * Porque o `zod` reprova em cascata e uma lista de três frases numa linha de
 * erro vermelho não é lida. A primeira é a que está mais acima no formulário, e
 * corrigi-la revela a seguinte no pedido seguinte — que é como um formulário
 * conversa.
 *
 * ## O que isto NÃO faz
 *
 * Não inventa frase: se o `details` não vier, ou não trouxer texto, fica a
 * mensagem do servidor tal como ela é. E não toca em recusa nenhuma que não seja
 * `validation_error` — as outras (`codigo_usado`, `codigo_expirado`,
 * `curso_nao_comprado`) já chegam com a frase certa e escrita à mão.
 */
function motivoDoServidor(
  corpo: { error?: string; message?: string; details?: unknown } | null,
  t: Tradutor = tradutorDe(null),
): string {
  const generica = corpo?.message ?? t('estado.naoDeuParaCompletar');
  if (corpo?.error !== 'validation_error') return generica;

  const questoes = corpo.details;
  if (!Array.isArray(questoes)) return generica;

  for (const q of questoes) {
    const frase = (q as { message?: unknown } | null)?.message;
    if (typeof frase === 'string' && frase.trim() !== '') return frase;
  }
  return generica;
}

async function pedir<T>(caminho: string, init?: RequestInit): Promise<T> {
  const resposta = await fetch(`${BASE}${caminho}`, {
    ...init,
    headers: {
      /*
       * `content-type` só quando há CORPO — e a ausência disto quebrava quatro
       * botões da área do aluno.
       *
       * O Fastify recusa com 400 (`FST_ERR_CTP_EMPTY_JSON_BODY`) qualquer
       * requisição que declare `application/json` e chegue vazia. Quatro chamadas
       * daqui são POST/DELETE sem corpo — **Sair**, **Marcar como concluída**,
       * **desmarcar** e **Emitir certificado** — e as quatro saíam com o
       * cabeçalho, levavam 400 e nunca chegavam ao handler.
       *
       * O sintoma escondia a causa em cada uma. "Sair" navegava para `/entrar`
       * mesmo assim, com a sessão viva no Redis — quem saía num computador
       * emprestado continuava logado. "Marcar como concluída" apenas não grudava.
       * Nenhum erro aparecia na tela, porque `BotaoConcluir` não trata falha e o
       * `Cabecalho` engole a recusa com `.catch(() => undefined)`.
       *
       * Achado ao construir "entrar como este aluno": a faixa de suporte depende
       * de `POST /sair` para devolver o operador ao painel, e foi a primeira vez
       * que alguém observou a resposta desta chamada.
       */
      ...(init?.body === undefined ? {} : { 'content-type': 'application/json' }),
      /* Só nos métodos que mudam estado — o servidor exige exatamente nesses. */
      ...(init?.method && init.method !== 'GET' ? { 'x-vestigo-aluno-csrf': csrf() } : {}),
      /*
       * ── E O CSRF DO PAINEL, QUANDO — E SÓ QUANDO — ELE EXISTE (`Q10`) ─────
       *
       * O tutor entra na comunidade **logado como tutor** (ordem do dono), e a
       * porta do mural corre `requireAuth` para ele. `requireAuth` cobra
       * `x-vestigo-csrf` em todo método que não é seguro, exatamente como cobra
       * em `/api/*` — a porta é outra, o portão é o mesmo.
       *
       * ## Por que os DOIS cabeçalhos, e por que isto não é um vazamento
       *
       * Porque quem manda o pedido não sabe qual das duas sessões o servidor vai
       * usar, e não deve saber: **é a `portaDoMural` que decide, pelo cookie**.
       * Uma tela que escolhesse o cabeçalho seria a segunda régua para a mesma
       * pergunta, e a que ficasse diferente seria a que manda o tutor a um 403
       * depois de ele escrever o texto.
       *
       * Não vaza nada: os dois são cookies **do próprio navegador**, para a
       * **mesma origem**, lidos por JS de propósito (double submit) e mandados
       * de volta ao mesmo servidor que os pôs. `csrfDoPainel()` devolve `''`
       * para todo aluno que nunca teve painel — que é toda a base — e um
       * cabeçalho vazio é o mesmo que nenhum para `requireMember`, que não o lê.
       */
      ...(init?.method && init.method !== 'GET' && csrfDoPainel()
        ? { 'x-vestigo-csrf': csrfDoPainel() }
        : {}),
      ...(init?.headers ?? {}),
    },
    credentials: 'same-origin',
  });

  if (resposta.status === 204) return undefined as T;

  const corpo = await resposta.json().catch(() => null);

  if (!resposta.ok) {
    throw new ErroDaApi(
      resposta.status,
      corpo?.error ?? 'erro',
      motivoDoServidor(corpo),
      corpo?.details ?? undefined,
    );
  }

  return corpo as T;
}

export const api = {
  get: <T>(caminho: string) => pedir<T>(caminho),
  post: <T>(caminho: string, corpo?: unknown) =>
    pedir<T>(caminho, { method: 'POST', body: corpo ? JSON.stringify(corpo) : undefined }),
  put: <T>(caminho: string, corpo: unknown) =>
    pedir<T>(caminho, { method: 'PUT', body: JSON.stringify(corpo) }),
  patch: <T>(caminho: string, corpo: unknown) =>
    pedir<T>(caminho, { method: 'PATCH', body: JSON.stringify(corpo) }),
  delete: <T>(caminho: string) => pedir<T>(caminho, { method: 'DELETE' }),

  /**
   * A batida de progresso do descarregamento da página.
   *
   * `keepalive` e **não** `sendBeacon`: o beacon não manda cabeçalho, e o CSRF
   * do aluno é um header. Um beacon aqui seria recusado com 403 justamente na
   * última batida — a que carrega os segundos que ninguém mais vai reportar.
   */
  progressoAoSair(caminho: string, corpo: unknown): void {
    void fetch(`${BASE}${caminho}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json', 'x-vestigo-aluno-csrf': csrf() },
      body: JSON.stringify(corpo),
      credentials: 'same-origin',
      keepalive: true,
    }).catch(() => undefined);
  },
};

// ---------------------------------------------------------------- contratos

export interface TemaDaArea {
  fundo: string;
  superficie: string;
  texto: string;
  textoSuave: string;
  destaque: string;
  raio: number;
}

export interface AreaDoSeletor {
  slug: string;
  nome: string;
  tema: TemaDaArea;
  capa: string;
}

export interface Eu {
  membro: {
    nome: string;
    email: string;
    /** CPF/CNPJ, ou o documento que a pessoa tiver. Nulo é o caso comum: só se
     *  preenche no perfil, e ninguém é obrigado. */
    documento: string | null;
    /**
     * O `@` do Instagram que a PESSOA declarou — normalizado (minúsculas, sem
     * `@`). Nulo é o caso comum, e é opcional de propósito.
     *
     * Não é identificador de ninguém: é uma declaração, e serve para o tutor
     * reconhecer quem ela é se já se falaram por lá. Quem resolve identidade é o
     * IGSID que o Meta manda, e esse não passa por aqui.
     */
    instagram: string | null;
    /**
     * Quando o convite do Instagram foi MOSTRADO a esta pessoa. ISO, e **`null` é
     * «nunca perguntámos»**.
     *
     * É este nulo que o convite do primeiro acesso lê. Ele não pode ser
     * confundido com `instagram === null` («não sabemos o `@` dela»): a primeira é
     * uma fila de quem ainda não viu, a segunda inclui quem viu e disse não.
     *
     * Desde a `0468` ela data o ÚLTIMO convite, e sozinha não decide mais nada:
     * quem decide é `instagramConvites`, e a régua dos dois está em
     * `@vestigo/core/area-convite-do-instagram`.
     */
    instagramPerguntadoEm: string | null;
    /**
     * Quantos convites já se MOSTRARAM — zero a cinco.
     *
     * Conta quem desenha, e não quem responde: quem fecha a aba sem clicar em
     * nada também viu o convite. Ver `member.instagramConvites` no schema.
     */
    instagramConvites: number;
  };
  areas: AreaDoSeletor[];
}

export interface CursoDaVitrine {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  aulas: number;
  /**
   * Quanto dura este curso, em SEGUNDOS — ou `null` quando não se diz (0439).
   *
   * `null` cobre três casos que a tela desenha igual: o tutor desligou o recurso
   * na área, este curso o desligou por conta própria, ou não há um segundo de
   * vídeo publicado nem carga fixada à mão. **Nunca `0`** — zero afirmaria que o
   * curso tem zero de conteúdo, e num curso de texto isso é falso e parece
   * verdadeiro.
   *
   * O servidor já aplicou a herança curso↔área e a precedência declarado↔medido
   * (`cargaHorariaVisivelNoCurso` e `cargaDoCurso`, no core). A tela não decide
   * nada: imprime, ou não imprime.
   *
   * Opcional porque a Prova do Estúdio monta este objeto à mão, sem servidor.
   */
  cargaSegundos?: number | null;
  capa: string | null;
  /**
   * Os degraus derivados da capa, para o `<img srcset>`.
   *
   * ## Por que o campo existe
   *
   * Porque `capa` sozinha desenhava o arquivo errado. Medido em produção: a
   * mesma imagem de **776 138 bytes** e 848×1264 servindo um cartaz de 300×450 e
   * um herói de 2033×762 — **1,9× o teto de 400 kB** da régua C9 (§15.7).
   *
   * String vazia é o estado normal enquanto o worker não derivou, e também o de
   * toda imagem que não rende derivado. Um `srcset` vazio é inofensivo: o
   * navegador usa o `src`. Opcional porque a Prova do Estúdio monta este objeto
   * à mão, sem servidor.
   */
  capaSrcset?: string;
  /**
   * As dimensões do ORIGINAL, para o `<img>` reservar a caixa antes do byte.
   *
   * Nulas até a primeira derivação — as colunas existiam no banco desde a
   * fundação e nunca tinham sido escritas por ninguém. Sem elas, trocar
   * `background-image` por `<img>` trocaria um defeito de peso por um de
   * deslocamento de layout.
   */
  capaLargura?: number | null;
  capaAltura?: number | null;
  /**
   * Este curso tem capa animada — e isto é um booleano, não o endereço.
   *
   * Resolver a prévia custa uma ida ao fornecedor (~1,05 s, medido em §17.9), e
   * a vitrine não vai pagar isso por cartaz que talvez ninguém toque. A tela
   * pede o endereço quando a pessoa demonstra interesse, um de cada vez, pela
   * rota `…/cursos/:id/previa`.
   *
   * Só vem `true` quando **também** há `capa`: a imagem é o primeiro quadro e o
   * que fica no lugar quando não há movimento. Ver `capa-animada.ts`.
   */
  previa?: boolean;
  /** A pessoa não tem este curso — ele aparece porque há página de venda. */
  bloqueado?: boolean;
  /**
   * Para onde o cartaz MANDA quem não tem o curso — a página de venda do tutor.
   *
   * Ordem do dono de 02/10/2026: *«deveria ter um redirecionamento para o link
   * cadastrado»*. O cartaz passou a ser um `<a href>` para cá, em vez de um
   * `<Link>` para `/c/:id` que ia pedir, levar 403 e só então desenhar o
   * convite com este mesmo endereço.
   *
   * **Só vem quando `bloqueado`**, e `null` nos outros casos: o servidor não
   * manda o endereço de venda de um curso que a pessoa já tem, porque ninguém
   * nesta tela o leria — e um campo sem leitor é o que algum dia desenha o
   * botão de comprar por cima do curso que ela pagou. Ver `routes/aluno.ts`.
   *
   * Ausente nos objetos que a prévia do Estúdio monta à mão, e aí o cartaz volta
   * a ser o `<Link>` de sempre.
   */
  oferta?: string | null;
  /**
   * O curso é dela e **espera outro** — a tranca por pré-requisito.
   *
   * Nada a ver com `bloqueado`, e os dois nunca são a mesma frase: ali falta
   * comprar (e o clique leva à venda), aqui falta *fazer*, e o clique não leva a
   * lado nenhum porque `exigirAula` recusa o corpo. Ver `lib/tranca.ts`.
   *
   * Ausente = aberto por esta régua. Quem o desenha passa-o por `lerTranca`
   * antes de ler campo nenhum: o `dist` do aluno e a API podem ser de levas
   * diferentes por uns minutos, e um campo em falta não pode apagar a
   * prateleira inteira.
   */
  tranca?: TrancaDeOrdem | null;
  /**
   * O curso tem **data de estreia** e ela ainda não chegou (24/09/2026).
   *
   * É a terceira razão de um cartão estar trancado, e a única que não fala de
   * um gesto de quem lê: não há o que comprar nem o que cumprir, só esperar.
   * Só chega aqui o curso que o tutor mandou **anunciar**; o que ele mandou
   * *guardar* não vem na lista, e por isso não tem desenho nenhum.
   *
   * `null` ou ausente = sem agendamento, que é o caso de quase todo curso — e
   * `null` **não é zero**. Ver `lib/lancamento.ts`.
   */
  lancamento?: Lancamento | null;
  /**
   * ── ESTE CARTÃO NÃO EXISTE PARA O ALUNO (04/11/2026) ─────────────────────
   *
   * > *«ele deveria ao menos aparecer na vitrine da edição uma capa com aviso de
   * > que ele está agendado e bloqueado na sua visualização»* — o dono
   *
   * Só chega na prévia da BANCADA (`GET /:id/previa?fonte=rascunho`), e marca o
   * curso em rascunho — inclusive o **guardado**, que é rascunho com hora
   * marcada — que o tutor arrastou para uma faixa à mão. Na vitrine do aluno o
   * campo não existe, e o objeto também não: a régua do core recusa o rascunho
   * antes de qualquer desenho, e essa lei não muda.
   *
   * Ausente ou `false` = o cartaz é o de sempre. Quem desenha lê-o por
   * `Boolean(...)`, nunca por `!`: um campo em falta significa «cartaz normal».
   */
  soVoceVe?: boolean;
  progresso: { percent: number; lessonsDone: number; lessonsTotal: number } | null;
}

/**
 * A marca da área, já em URLs assinadas.
 *
 * O que está gravado é o id do `media_asset`; o servidor assina na hora, porque
 * a URL vence em horas e gravá-la deixaria o cabeçalho sem logo numa tarde
 * qualquer. Tudo nulo = o cliente não escolheu nada, e a tela mostra o nome da
 * área em texto — que é o certo: uma marca nossa no topo do curso que alguém
 * vendeu seria a nossa marca no produto dele.
 *
 * ## Por que os três campos são OBJETO, e o que isso conserta
 *
 * Porque é o que a rota manda: `GET /a/:slug` resolve a marca no mesmo pedido
 * das capas, e aquele mapa devolve `CapaServida` — `{ url, srcset, padrao,
 * largura, altura, animada }`. Enquanto isto dizia `string | null`, o cabeçalho
 * escrevia `src={marca.logo}`, o compilador concordava, e a área servia
 * `<img src="[object Object]">` em produção (medido em 23/09/2026). Declarado
 * como objeto, a mesma linha deixa de compilar.
 *
 * Quem desembrulha é `marcaDaArea`, em `@vestigo/core/area-marca`, e é o único
 * lugar autorizado a fazê-lo — ver o cabeçalho daquele arquivo.
 */
export type { ImagemDaMarca };

export interface MarcaDaArea {
  logo: ImagemDaMarca | null;
  favicon: ImagemDaMarca | null;
  compartilhamento: ImagemDaMarca | null;
}

/** Um item do menu do topo. `url` já passou pela lista de esquemas do servidor. */
export interface ItemDeMenu {
  rotulo: string;
  url: string;
}

/** O resumo de uma trilha na vitrine. O percentual é por AULA — ver o core. */
export interface TrilhaNaVitrine {
  slug: string;
  title: string;
  subtitle: string | null;
  percentual: number;
  cursos: number;
  /** "Continuar em X" / "Começar por Y" — a frase vem do servidor. */
  frase: string;
  proximoCursoId: string | null;
  /**
   * ── OS DOIS LADOS DA FRAÇÃO, E O LUGAR NA SEQUÊNCIA (`0467`) ──────────────
   *
   * `aulasTotal` é o **denominador**, e ele viaja para matar uma dedução: a
   * página deduzia «há medida?» de `proximoCursoId === null && percentual === 0`.
   * A dedução estava certa, e era isso que a tornava perigosa — dependia de um
   * invariante de `proximaEtapa`, noutro ficheiro, que ninguém obrigava a durar.
   *
   * `etapaAtual` é **1-based** e **`null` quando não há próximo**. Nunca `0`:
   * zero não é uma posição, e «etapa 0 de 5» é o zero fabricado desta tela.
   *
   * ## Por que OPCIONAIS, e isto não é folga — é o segundo emissor
   *
   * Dois corpos chegam a esta forma. `GET /a/:slug/trilhas` (a rota do aluno)
   * manda os três, sempre. A **Prova do Estúdio** (`areas.ts`) monta a lista com
   * `percentual: 0` e `proximoCursoId: null` porque *«não há uma pessoa aqui»* —
   * e, pela mesma razão, não tem numerador nem posição para mandar.
   *
   * Então a ausência é um estado de verdade, e tem de se poder dizer. `?` e não
   * `| 0`: um zero no lugar do ausente faria a Prova desenhar «0 de 0 aulas»
   * sobre uma trilha que tem vinte e quatro. Quem lê **tem de perguntar se o
   * número existe** antes de o usar — ver `temMedida`, em `TrilhasDaArea`.
   */
  aulasTotal?: number;
  aulasConcluidas?: number;
  etapaAtual?: number | null;
  /**
   * ── O CARTAZ DA TRILHA, JÁ ASSINADO (08/10/2026) ──────────────────────────
   *
   * A `0467` deu à trilha um `cover_media_id`, e o cartão da prateleira
   * desenha-o desde então. A **página** das trilhas recebia o id cru e não
   * tinha o que fazer com ele; agora `GET /a/:slug/trilhas` resolve-o por
   * `resolverCapasServidas`, a mesma que recusa o expirado e o que não é
   * imagem.
   *
   * Opcional pelo motivo do bloco acima — há um segundo emissor. E `capa: null`
   * **não é o mesmo que ausente**: null é «esta trilha não tem cartaz», e é
   * sobre isso que a tela decide desenhar a arte ou a linha inteira de texto.
   */
  capa?: string | null;
  capaSrcset?: string;
  capaLargura?: number | null;
  capaAltura?: number | null;
  /**
   * A trilha inteira espera outro conteúdo (23/09/2026, ordem do dono: *«as
   * trilhas também podem ser configuradas dependendo de outros cursos
   * anteriores»*).
   *
   * Com ela o cartão deixa de ser link e a frase de próxima etapa dá lugar ao
   * motivo: prometer «Continuar em X» numa trilha que não abre seria a tela a
   * oferecer o que o servidor recusa.
   */
  tranca?: TrancaDeOrdem | null;
  /**
   * Só a **prévia do Estúdio** manda este campo.
   *
   * A rota do aluno nunca devolve trilha em rascunho, então ele nunca chega
   * aqui — e é por isso que ele é opcional em vez de um segundo tipo. A prévia
   * precisa das rascunho porque ela é a única porta para o editor: uma trilha
   * recém-criada nasce rascunho, e sem elemento no DOM não haveria como abri-la.
   */
  rascunho?: boolean;
}

export interface CursoDaTrilhaNaTela {
  id: string;
  title: string;
  subtitle: string | null;
  capa: string | null;
  /** Ver `capaSrcset` em `CursoDaVitrine`: a etapa desenha o mesmo cartaz. */
  capaSrcset?: string;
  capaLargura?: number | null;
  capaAltura?: number | null;
  aulas: number;
  concluidas: number;
  percent: number;
  /** O curso emite certificado. Sobre o CURSO — não sobre esta pessoa. */
  certificado: boolean;
  /**
   * A etapa **ainda não estreou** (24/09/2026) — um curso anunciado no meio de
   * um percurso, que é o caso mais provável de todos.
   *
   * ## Opcional, e neste momento nunca preenchido — está escrito por quê
   *
   * A rota da trilha **calcula** o `lancamento` (`aluno.ts`, no `comTranca`) e
   * perde-o antes do corpo: `cursos:` monta os campos um a um e não o inclui, e
   * `marcosDaTrilha` só copia para o marco o que o marco conhece — `tranca`. O
   * desenho desta ponta está pronto e cercado; falta o campo chegar aqui, onde
   * a `Etapa` já tem o objeto do curso na mão.
   *
   * E falta mais uma coisa, do lado de lá: `alcancaveis` filtra só por `tranca`,
   * então o «você está aqui» e o «Continuar em X» ainda podem apontar para um
   * curso que o servidor recusa — o mesmo defeito que a tranca de ordem
   * conserta uma linha acima.
   */
  lancamento?: Lancamento | null;
}

/* `extends SinaisDaBarra` desde 08/10/2026: a barra desta página saía sem as
   quatro portas, e o que faltava não era um `if` — era a resposta não trazer os
   sinais. Herdar é o que faz a tela seguinte não os esquecer. */
export interface TrilhaDetalhada extends SinaisDaBarra {
  trilha: {
    slug: string;
    title: string;
    subtitle: string | null;
    description: string | null;
    capa: string | null;
    capaSrcset?: string;
    capaLargura?: number | null;
    capaAltura?: number | null;
  };
  tema: TemaDaArea;
  /**
   * A proporção dos cartazes desta área — `3/4` por omissão.
   *
   * **Opcional porque o corpo pode ser de outra leva**: o `dist` do aluno e a
   * API podem andar uns minutos separados num deploy. Ausente, a tela usa o
   * mesmo padrão que o core usa (`aspectoDaCapa` já o faz), e não um valor
   * inventado aqui — duas omissões diferentes para a mesma falta é como as duas
   * telas passam a recortar a arte de maneiras distintas.
   */
  capa?: ProporcaoDeCapa;
  progresso: {
    percentual: number;
    aulasConcluidas: number;
    aulasTotal: number;
    cursosConcluidos: number;
    cursosTotal: number;
    concluida: boolean;
  };
  etapa: {
    tipo: 'continuar' | 'comecar' | 'concluida' | 'vazia';
    courseId?: string;
    frase: string;
  };
  cursos: CursoDaTrilhaNaTela[];
  /**
   * O trajeto, calculado por `marcosDaTrilha` no core.
   *
   * Chega pronto do servidor de propósito: "você está aqui" é a mesma decisão do
   * botão (`proximaEtapa`), e refazê-la aqui criaria a segunda régua — a que
   * esquece de quem pulou o curso 1 e prende o marco lá para sempre.
   */
  marcos: MarcoDaTrilha[];
}

/**
 * ═══ OS SINAIS QUE TODA TELA DA ÁREA MANDA À BARRA ═════════════════════════
 *
 * Ordem do dono de 01/10/2026 (*«o menu de minhas aulas na barra superior deve
 * ter os mesmos links … como está em agenda»*) e de 08/10/2026 (*«no menu top
 * da página da trilha, devem existir os links de comunidade, agenda, minhas
 * aulas, trilhas como é na vitrine»*). **A mesma ordem, duas vezes, sobre telas
 * diferentes** — e por isso o que fica desta vez não é o conserto da segunda
 * tela: é o nome.
 *
 * A forma estava escrita à mão em três sítios (a vitrine, «Minhas aulas» e a
 * casca de `/trilhas`), e a quarta tela escreveu-a com um campo a menos — que é
 * precisamente como três cópias se tornam quatro divergências. Agora há um tipo,
 * e a tela nova herda-o inteiro ou não compila.
 *
 * ## Os QUATRO são opcionais, e isso é uma afirmação sobre o deploy
 *
 * Nenhuma destas duas rotas mandava campo nenhum destes até hoje. O `dist` do
 * aluno e a API andam minutos separados num deploy, e nesse intervalo a aba
 * aberta recebe a resposta antiga — exigir `menu` aqui seria o tipo a afirmar
 * sobre a rede uma coisa que a rede não cumpre. «Não sei» desenha a barra mais
 * curta; nunca a esconde: ver `PortasDaArea`, onde `comunidadeAberta: undefined`
 * **não** fecha a porta.
 *
 * ## ⚠ E POR ISSO ESTE TIPO NÃO GARANTE NADA — ele é vocabulário
 *
 * Uma interface só de opcionais é satisfeita por **qualquer** objeto. Escrever
 * `extends SinaisDaBarra` não obriga tela nenhuma a mandar os sinais, e dizer o
 * contrário seria a marca de tipo inerte: o compilador parece estar a guardar
 * alguma coisa e não está.
 *
 * O que ela dá é **um nome e um sítio** — a quinta tela da área herda os quatro
 * campos com as quatro explicações em vez de inventar três e esquecer o quarto,
 * que foi o que aconteceu duas vezes. Quem GUARDA a lei é a varredura de
 * `toda-tela-da-area-carrega-as-quatro-portas`, que lê a fonte e pergunta se a
 * tela que monta `<Cabecalho area=…>` também monta `<PortasDaArea>`.
 */
export interface PortasDaBarra {
  temTrilhas: boolean;
  comunidadeAberta: boolean;
  cracha: { quantos: number; rotulo: string } | null;
}

export interface SinaisDaBarra {
  menu?: ItemDeMenu[];
  /** O cabeçalho (S5): modelo, marca e fixo. Ausente num corpo antigo = clássico. */
  cabecalho?: { modelo?: string; marca?: string; fixo?: boolean };
  /** Há a quem perguntar nesta área? Ausente lê-se como desligado. */
  assistente?: boolean;
  /** Agenda · Comunidade · Minhas aulas · Trilhas. Ver `portasDaBarra`, na API. */
  portas?: PortasDaBarra;
}

/** A cara da tela de entrar, pedida SEM sessão. Ver `/entrada/:areaSlug`. */
export interface TelaDeEntrada {
  nome: string | null;
  tema: TemaDaArea;
  /**
   * A porta pública de cadastro está aberta? — `CE3`. Ausente num corpo antigo
   * (e na prévia do Estúdio) = a tela não desenha a porta, que é o lado seguro:
   * um link para uma tela que responde 404 é pior que link nenhum.
   */
  cadastroAberto?: boolean;
  entrada: {
    /** Uma das seis (leva L1). Ausente num corpo antigo: a moldura deriva de `orientacao`. */
    estrutura?: 'sobria' | 'lateral' | 'veu' | 'cartao' | 'editorial' | 'faixa';
    imagem: string | null;
    imagemSrcset?: string;
    imagemLargura?: number | null;
    imagemAltura?: number | null;
    /** LEGADO: o servidor deixou de mandar na leva F; a moldura já decide por `estrutura`. */
    orientacao?: 'centro' | 'lateral';
    chamada: string;
    titulo?: string | null;
    cores?: {
      fundo: string | null;
      superficie: string | null;
      destaque: string | null;
      texto: string | null;
    };
    fonte?: 'sistema' | 'serifa' | 'mono' | 'literata';
    tamanho?: 'compacto' | 'normal' | 'amplo';
    icone?:
      | 'cadeado'
      | 'chave'
      | 'porta'
      | 'estrela'
      | 'livro'
      | 'play'
      | 'raio'
      | 'folha'
      | 'escudo'
      | 'graduacao'
      | 'faisca'
      | 'coracao'
      | null;
    veu?: number;
    raio?: number | null;
  };
  /**
   * O logo da área. **Não desenhe direto** — passe por `imagemDaMarca`.
   *
   * A união com `ImagemDaMarca` não é hipótese: a rota da vitrine já manda o
   * objeto, esta manda o endereço (`resolverCapas`), e o dia em que a tela de
   * entrar quiser `srcset` a troca é de uma linha no servidor. Declarada como
   * união, `src={cara.logo}` não compila, e o desembrulhador — que aceita as
   * duas formas — é o único caminho. É a lição do `[object Object]`, escrita
   * onde ela ainda não tinha custado nada.
   */
  logo: ImagemDaMarca | string | null;
}

/**
 * ── A CARA DO CADASTRO ESPONTÂNEO (`CE1`) ───────────────────────────────────
 *
 * O espelho de `TelaDeEntrada`, e por isso ele ESTENDE-a em vez de a repetir: o
 * contrato diz, com todas as letras, que o bloco `cadastro` reusa os tipos de
 * `entrada` — *«duas listas de fontes divergem na primeira que alguém
 * acrescentar a uma só»*. Herdar aqui é a mesma frase escrita em TypeScript: a
 * sétima estrutura, a quinta fonte e o décimo terceiro ícone chegam às duas
 * telas no mesmo dia, ou a nenhuma.
 *
 * As duas chaves a mais, e por que cada uma é opcional:
 *
 * - `aberto`: no caminho normal ela **não viaja** — porta fechada responde 404,
 *   e um 200 já quer dizer aberto. Ela existe para o servidor poder dizê-lo
 *   explicitamente sem a tela precisar de mudar.
 * - `boasVindas`: declarada nos dois sítios possíveis porque o contrato fixou
 *   que ela existe e não fixou onde ela cai no corpo da resposta. Ver
 *   `boasVindasDoTutor` em `rotas/Cadastro.tsx`.
 */
export interface TelaDeCadastro extends TelaDeEntrada {
  aberto?: boolean;
  boasVindas?: string | null;
  entrada: TelaDeEntrada['entrada'] & { boasVindas?: string | null };
}

/** Um cartão de prateleira já resolvido pelo servidor. Ver `ItemDesenhado` na API. */
export type ItemDaPrateleira =
  | { tipo: 'curso'; id: string }
  /**
   * A TRILHA na prateleira (0467) — e o objeto INTEIRO vem aqui.
   *
   * O curso vem só pela chave porque a tela já o tem em `cursos`, a lista que a
   * vitrine desenha. A trilha não está em lista nenhuma do corpo: ela é um item
   * de faixa e nada mais.
   *
   * `percentual` e `tranca` vêm da MESMA conta que a página da trilha usa
   * (`trilhasDoAluno`, chamada antes de montar as faixas) — recontá-las faria o
   * cartão dizer 39% e a página 40% sobre a mesma trilha.
   */
  | {
      tipo: 'trilha';
      id: string;
      slug: string;
      titulo: string;
      /** «4 cursos» — `legendaDoItem`, no core. */
      linha: string | null;
      /** O caminho dentro da área: `/trilha/<slug>`. */
      para: string;
      /** 0–100, do CONJUNTO. A mesma grandeza que a `Barra` do curso desenha. */
      percentual: number;
      /** As MESMAS peças do curso — é por isso que o cartaz fala a mesma língua. */
      tranca: TrancaDeOrdem | null;
      lancamento: Lancamento | null;
      /**
       * Sem acesso e com página de venda: o cartaz aparece para VENDER, como o
       * do curso.
       *
       * A trilha herda a excepção do curso, e módulo e aula continuam a não a
       * herdar — a razão está em `prateleira-itens.ts`: num módulo o cartão
       * diria «Aula 3 · 12 min» e o clique abriria um checkout de curso, que é
       * promessa trocada; na trilha o cartão diz «Trilha · 4 cursos» e o clique
       * abre a venda DAQUELA trilha. É o que «comercializada como os cursos
       * são» significa.
       *
       * Quando ele é `true`, `tranca` vem `null` de propósito: a voz do cartão
       * é uma só, e «Continuar em X» numa trilha que não é dela seria prometer
       * um gesto que o servidor recusa.
       */
      comoVenda: boolean;
      /** O endereço da venda. Não nulo só quando `comoVenda`. */
      oferta: string | null;
      capa: string | null;
      capaSrcset: string;
      capaLargura: number | null;
      capaAltura: number | null;
    }
  | {
      tipo: 'modulo' | 'aula';
      id: string;
      cursoId: string;
      cursoTitulo: string;
      titulo: string;
      /** «3 aulas», «12 min» — ou nada, quando não há número honesto. */
      linha: string | null;
      /** O caminho dentro da área: `/aula/<id>`. */
      para: string;
      aulaTipo: TipoDeAula | null;
      /**
       * A aula está aqui porque é **amostra grátis** de um curso que esta pessoa
       * não tem (02/10/2026) — e o cartaz di-lo com um carimbo.
       *
       * Nunca `true` para quem tem o curso: lá a mesma aula é só mais uma aula.
       * E o cartaz **não** perde a cor nem ganha véu, ao contrário do curso sem
       * acesso: esta aula ABRE, e o véu é a linguagem do que não abre. Ver
       * `SeloDeAmostra`, em `Oferta.tsx`.
       */
      amostra?: boolean;
      capa: string | null;
      capaSrcset?: string;
      capaLargura?: number | null;
      capaAltura?: number | null;
    };

/** Um slide do herói, pronto para desenhar. Ver `SlideResolvido` na API. */
export interface SlideDoHeroi {
  chave: string;
  /**
   * `banner` é o cartaz do dono (leva H): não é item, não leva a lugar nenhum.
   * `trilha` entrou em 08/10/2026 — o destaque que o dono pediu.
   *
   * A tela não ganha ramo novo por causa dela: o servidor já manda `para`,
   * `titulo`, `linha`, `botao`, `progresso` e a capa resolvidos, e é essa a lei
   * da camada — fato é do servidor, forma é da tela. O que o herói desenha para
   * uma trilha é o que ele desenha para um curso, com `/trilha/<slug>` no
   * `para` e «4 cursos» na `linha`.
   */
  tipo: 'curso' | 'modulo' | 'aula' | 'trilha' | 'banner';
  id: string;
  cursoId: string | null;
  /**
   * O caminho dentro da área: `/c/<id>` ou `/aula/<id>` — **`null` no cartaz**.
   *
   * «Sem link» é este `null`, e não a ausência de um `href`: é ele que decide
   * que não há `<a>`, que não há botão e que a região se declara não clicável.
   */
  para: string | null;
  titulo: string;
  texto: string | null;
  linha: string | null;
  botao: 'Começar' | 'Continuar' | null;
  imagem: string | null;
  imagemSrcset?: string;
  imagemLargura?: number | null;
  imagemAltura?: number | null;
  /** O arquivo se mexe sozinho (GIF): quem pediu menos movimento vê um quadro. */
  animada?: boolean;
  escurecer: number;
  progresso: number | null;
  /** O corte do dono: ponto focal em % e zoom. Ausente/`null` = o centro, com a deriva. */
  enquadramento?: { x: number; y: number; zoom: number } | null;
  /** O corte abaixo de 640 px; ausente/`null` = o da tela. */
  enquadramentoCelular?: { x: number; y: number; zoom: number } | null;
  /** `preencher` | `cartaz` (a capa inteira ao lado); ausente/`null` = automático. */
  encaixe?: 'preencher' | 'cartaz' | null;
}

export interface SecaoDaVitrine {
  tipo: 'heroi' | 'saudacao' | 'retomada' | 'prateleira' | 'trilhas';
  id: string | null;
  tom: 'fundo' | 'superficie' | 'claro';
}

export interface Vitrine {
  /** Ver `MedicaoDaPagina`. */
  medicao?: MedicaoDaPagina | null;
  area: { slug: string; nome: string };
  /**
   * A ordem e o tom das seções, já resolvidos pelo servidor (leva A2). Ausente
   * num corpo antigo: a tela cai na mesma regra com layout vazio.
   */
  secoes?: SecaoDaVitrine[];
  tema: TemaDaArea;
  capa: string;
  /** O tamanho do cartaz, escolhido pelo tutor. Ausente = `medio`, que é a
   *  vitrine que já estava no ar. A prévia do Estúdio pode não mandá-lo. */
  capaTamanho?: TamanhoDeCapa;
  marca: MarcaDaArea;
  /**
   * ── O ACERVO DESTA PESSOA, EM TEMPO (0439) ────────────────────────────────
   *
   * A linha discreta do rodapé da vitrine: quantos cursos o acesso desta pessoa
   * abre, e quanto tudo isso soma.
   *
   * **`null` = o tutor não ligou o recurso na ÁREA**, e aí o total não existe —
   * mesmo que um curso o ligue por conta própria. Um total rotulado «o seu
   * acesso» numa área que declinou o recurso mediria um curso e falaria do todo.
   *
   * Os três campos, e não uma frase pronta: quem desenha escolhe as palavras.
   * `comCarga` é o que permite dizer «108h 40 **em 12 deles**» em vez de «no
   * total» sobre catorze cursos — a mesma lei do denominador declarável do
   * V-View. `segundos` é `null` quando nenhum curso entrou na soma, e aí a
   * contagem de cursos continua a informar sozinha.
   *
   * Opcional: a Prova do Estúdio e um corpo de resposta anterior à 0439 não o
   * trazem, e ausente lê como «não desenhe nada».
   */
  acervo?: {
    cursos: number;
    comCarga: number;
    segundos: number | null;
    /**
     * Quanto esta pessoa já assistiu, nos MESMOS cursos que entraram em
     * `segundos`. `null` quando não há denominador — sem ele o numerador não
     * afirma nada.
     */
    assistido: number | null;
  } | null;
  saudacao: string;
  menu: ItemDeMenu[];
  /**
   * Há a quem perguntar nesta área? (`cfg.ia.assistente === "sim"`)
   *
   * Viaja na resposta da vitrine desde 30/09/2026, quando a porta do tutor
   * passou a existir fora da aula. **Opcional**, e a ausência lê-se como
   * desligado: a prévia do Estúdio não a manda, e ali não há sessão de aluno
   * para uma porta paga poder existir.
   */
  assistente?: boolean;
  /**
   * A trilha PESSOAL em curso — um cartão na faixa «Continue de onde parou».
   *
   * `null`/ausente = ele não tem nenhuma por terminar, e a faixa volta a ser só
   * as aulas em andamento. Note que a presença dela **faz a faixa existir**: o
   * servidor conta-a em `secoes`, porque salvar uma trilha enche a fila sem
   * começar aula nenhuma.
   */
  minhaTrilha?: {
    id: string;
    titulo: string;
    feitas: number;
    total: number;
    proxima: { aulaId: string; titulo: string } | null;
  } | null;
  /** O cabeçalho (S5): modelo, marca e fixo. Ausente num corpo antigo = clássico. */
  cabecalho?: { modelo?: string; marca?: string; fixo?: boolean };
  /**
   * ── O SINAL DA COMUNIDADE, NA PORTA (`PER-03`, `J1299` c6) ────────────────
   *
   * O crachá conta uma LISTA FECHADA de avisos dirigidos (`AVISOS_PARA_VOCE`, no
   * núcleo): as perguntas minhas com resposta nova, as menções a mim, e as
   * respostas minhas marcadas como a que resolveu. **Viajam o número e a frase
   * que diz o que ele conta** — as palavras ficam no servidor, porque esta tela
   * vive no pedaço de entrada. **`null` ou ausente = não desenhe nada.**
   *
   * Opcional porque a prévia do Estúdio monta esta mesma tela sem servidor: um
   * campo obrigatório aqui poria um crachá por decidir numa tela de desenho.
   */
  comunidade?: {
    cracha?: CrachaDaComunidade | null;
    /**
     * A PORTA, por grupo de acesso (0432). `true` = esta pessoa alcança a
     * comunidade desta área; `false` = não alcança, e a porta **não se desenha**.
     *
     * **Ausente é «não sei», e «não sei» desenha.** É o caso da prévia do Estúdio
     * e de um corpo de resposta anterior à 0432 — e esconder num «não sei»
     * repetiria o defeito que o telemóvel já pagou, onde um 403 de termos apagou
     * a aba de uma escola que tinha comunidade. Ver `PortasDaArea`.
     */
    aberta?: boolean;
  };
  trilhas: TrilhaNaVitrine[];
  heroi: {
    curso: string | null;
    titulo?: string;
    texto?: string;
    imagem: string | null;
    /** Os degraus da imagem do herói. Ver `capaSrcset` em `CursoDaVitrine`. */
    imagemSrcset?: string;
    imagemLargura?: number | null;
    imagemAltura?: number | null;
    escurecer: number;
    /**
     * O carrossel (leva A3), já resolvido pelo servidor. Ausente num corpo
     * antigo: a tela deriva um slide de `curso/imagem/titulo/texto`.
     */
    slides?: SlideDoHeroi[];
    altura?: 'faixa' | 'curta' | 'baixa' | 'media' | 'alta';
    avanco?: 'manual' | 'automatico';
    intervaloSegundos?: number;
  } | null;
  /**
   * A próxima aula a assistir de cada curso em andamento — "Continue de onde
   * parou".
   *
   * Aulas, e não cursos: o nome da prateleira fala de onde a pessoa **parou**, e
   * ninguém para num curso. **Uma por curso**, até cinco, da batida mais recente
   * para a mais antiga. Quem decide qual é a regra `retomada.ts`, no core: pode
   * ser a que ficou pela metade (`retomar`) ou a próxima de quem concluiu o que
   * tinha aberto (`seguir`).
   *
   * Opcional porque a **prévia do Estúdio** monta este objeto à mão, sem servidor
   * — e ela não pode quebrar por causa de um campo que só a vitrine real tem.
   */
  continuando?: {
    lessonId: string;
    courseId: string;
    title: string;
    kind: TipoDeAula;
    curso: string;
    /**
     * Por que esta aula. `retomar` = parou no meio dela; `seguir` = concluiu o
     * que tinha aberto e esta é a próxima.
     *
     * Opcional para a prévia do Estúdio, que não tem pessoa e por isso não tem
     * motivo. Ausente lê-se como `retomar`, que é o que a vitrine mostrava antes
     * de `seguir` existir.
     */
    motivo?: 'retomar' | 'seguir';
    percent: number;
    capa: string | null;
    capaSrcset?: string;
    capaLargura?: number | null;
    capaAltura?: number | null;
    duracao: number | null;
    retomarDe: number | null;
    /**
     * O curso desta linha espera outro (24/09/2026).
     *
     * Parece impossível — como é que se «continua» um curso trancado? — e
     * acontece pelo caminho mais comum de todos: o tutor cria o pré-requisito
     * **depois**, sobre gente que já começou. Vem o FATO, e não a decisão de
     * esconder: o progresso é dela, e quem desenha decide o que mostrar.
     */
    tranca?: TrancaDeOrdem | null;
  }[];
  /** A primeira de `continuando`. Continua existindo porque o schema da vitrine
   *  e a prévia do Estúdio ainda a leem. */
  continuar: {
    lessonId: string;
    courseId: string;
    title: string;
    curso: string;
    percent: number;
    retomarDe: number | null;
  } | null;
  /**
   * Os cursos que a pessoa vê, uma vez cada.
   *
   * As prateleiras referenciam por id porque o mesmo curso aparece em mais de
   * uma, e repetir o objeto mandaria a capa assinada duas vezes pela rede.
   */
  cursos: CursoDaVitrine[];
  prateleiras: {
    id: string;
    title: string;
    kind?: string;
    /** A projeção de cursos da faixa — o que sempre saiu. */
    cursos: string[];
    /**
     * A aparência DESTA faixa (a prateleira vence a área por eixo). Ausente =
     * a da área, que é o que a prévia antiga do Estúdio manda.
     */
    proporcao?: string;
    tamanho?: TamanhoDeCapa;
    /** O formato do cartão desta faixa (S4). Ausente = herda: o raio do tema, sem borda, sem sombra. */
    cartao?: { formato: string | null; borda: string | null; sombra: string | null };
    /**
     * Os cartões, na ordem — curso, módulo ou aula (leva A1 da bancada). Ausente
     * = só `cursos`, para um corpo montado por um painel mais antigo.
     */
    itens?: ItemDaPrateleira[];
  }[];
}

export type TipoDeAula =
  'video' | 'texto' | 'pdf' | 'audio' | 'embed' | 'live' | 'quiz' | 'galeria' | 'flashcards';

/** Já assinada pelo servidor — o uuid do arquivo não atravessa a ponte. */
export interface ImagemDaGaleria {
  url: string;
  legenda?: string | null;
}

export interface Cartao {
  frente: string;
  verso: string;
}

/** A pergunta como o aluno a recebe. Note o que não está aqui. */
export interface PerguntaDoAluno {
  id: string;
  prompt: string;
  kind: 'unica' | 'multipla';
  opcoes: { id: string; label: string }[];
}

export interface ProvaDoAluno {
  prova: PerguntaDoAluno[];
  passScore: number;
  maxAttempts: number | null;
  tentativas: { id: string; score: number; passed: boolean; finishedAt: string }[];
  podeEnviar: boolean;
  /** A frase de por que não pode. `null` quando pode. */
  motivo: string | null;
  /**
   * ── O QUE ESTA PROVA TRANCA, DITO PELO SERVIDOR ──────────────────────────
   *
   * A frase vem PRONTA daqui, pelo mesmo molde do `tranca` da etapa da trilha
   * (`TrajetoDaTrilha`), e pela mesma lei — a **R5-bis**: a tela não redige o
   * motivo. Se a recusa do servidor diz uma coisa e o aviso dentro da prova
   * diz outra sobre a mesma porta, o aluno fica com duas versões e nenhuma
   * delas é de quem decide.
   *
   * `null` ou ausente = **esta prova não tranca nada, e a tela não inventa**.
   * Um aviso a adivinhar é pior do que aviso nenhum: assusta sem informar.
   *
   * **O servidor passou a mandá-lo em 20/09/2026** — `trancaDoAvancoDestaProva`,
   * em `apps/api/src/routes/aluno.ts`. Continua opcional porque uma prova que
   * não tranca nada devolve `null`, e aí o aviso não existe: sem tranca, não se
   * inventa uma. Contrato em `docs/member-prova-do-aluno-contrato.md`.
   *
   * A frase diz o que CONTINUA FECHADO, e nunca que passar abre — porque com
   * pré-requisito na mesma porta «passar abre» seria falso.
   */
  trancaDoAvanco?: { frase: string } | null;
  /**
   * ── A TENTATIVA ABERTA: o sorteio fixo e o que já foi respondido ─────────
   *
   * O sorteio renova-se **a cada tentativa** e fica fixo enquanto ela está
   * aberta; `prova` acima JÁ é o sorteio desta tentativa. O que falta à tela é
   * o que o aluno já marcou, para retomar onde parou.
   *
   * Isto é o que impede a tela de oferecer, sem querer, um «sortear até
   * gostar»: recarregar devolve a mesma prova e as mesmas respostas. Sem este
   * campo a tela começa em branco e **não promete retomada nenhuma**.
   *
   * **O servidor passou a mandá-lo em 20/09/2026**: o `GET` abre a tentativa,
   * congela o sorteio em `quiz_attempt.questoes_sorteadas` e nos acessos
   * seguintes lê. Continua opcional porque o limite de tentativas esgotado não
   * abre tentativa nenhuma — e aí a prova é de leitura.
   */
  tentativaAberta?: { id: string; respostas: Record<string, string[]> } | null;
}

export interface ResultadoDaProva {
  score: number;
  passed: boolean;
  passScore: number;
  /**
   * A aprovação concluiu a aula — o servidor gravou a conclusão (0282).
   *
   * Ausente ou falso numa reprovação e numa sessão de suporte, que corrige sem
   * gravar. É o que a sala lê para acender a marca e o contador de avanço.
   */
  concluiuAula?: boolean;
  detalhe: {
    questionId: string;
    certa: boolean;
    corretas?: string[];
    explanation?: string | null;
  }[];
}

export interface AulaDaLista {
  id: string;
  title: string;
  kind: TipoDeAula;
  /**
   * ── A ESPÉCIE DA AVALIAÇÃO (0431): treino, e não prova ────────────────────
   *
   * `true` = **simulado**. Ele não entra na média do certificado, não conta no
   * pré-requisito e **nunca tranca** — há um CHECK no banco a garanti-lo. Só
   * tem sentido com `kind: 'quiz'`.
   *
   * Vem do SERVIDOR, e a tela não o deduz de `notaMinima` nem de
   * `maxAttempts`: uma prova de treino pode ter exatamente os mesmos números
   * de uma que decide, e adivinhar a espécie a partir deles seria a tela a
   * dizer «isto não conta» sobre algo que conta.
   *
   * Opcional porque **todo `quiz` era prova** antes de 24/09/2026: um corpo
   * antigo, servido de cache, e a prévia do Estúdio, que monta a aula à mão,
   * não trazem o campo. Ausente lê-se como prova — o lado que não promete
   * dispensa nenhuma.
   */
  simulado?: boolean;
  durationSeconds: number | null;
  /** Miniatura 16:9, já assinada. Nula = a aula não tem, e a lista mostra só texto. */
  capa: string | null;
  progresso: { percent: number; concluida: boolean };

  /**
   * Gotejamento. A aula bloqueada **vem na lista** — de propósito.
   *
   * Esconder o que ainda não abriu é mais simples e produz a pergunta "cadê o
   * módulo 3?". A antecipação é o mecanismo: quem vê que falta uma semana volta
   * numa semana.
   *
   * `frase` é o texto que o servidor montou; `liberaEm` é o instante em ISO, para
   * a tela poder refazer a frase no fuso de quem está lendo.
   */
  bloqueada: boolean;
  liberaEm: string | null;
  frase: string | null;
  /**
   * Quantos materiais complementares a aula tem (ordem do dono, 23/09/2026:
   * *«as aulas que tiverem material anexado como complemento devem ter um ícone
   * ou marcação de anexo na sua listagem»*).
   *
   * **Número, e não booleano**: é a mesma consulta, e é ele que deixa a tela
   * dizer «2 materiais» no dia em que quiser, sem uma segunda obra no servidor.
   *
   * Opcional porque a lista é desenhada também pela PRÉVIA do painel, que monta
   * este objeto à mão — e `undefined` ali quer dizer «este caminho não sabe»,
   * que é exatamente o que ele é. Ausente não desenha clipe nenhum.
   */
  materiais?: number;
}

/**
 * A frase do cadeado, refeita no fuso do navegador.
 *
 * O servidor manda uma pronta, e ela erra por algumas horas perto da virada do
 * dia porque conta em UTC. Aqui o `Intl` sabe onde a pessoa está, então esta
 * vence — e a do servidor fica como o que sobra se o `liberaEm` não vier.
 */
export function fraseDoCadeado(
  liberaEm: string | null,
  deReserva: string | null,
  t: Tradutor = tradutorDe(null),
  locale = 'pt-BR',
): string {
  /* A conta do dia local mudou-se para `lancamento.ts` em 24/09/2026, quando o
     CURSO ganhou data de estreia e passou a precisar dela também. Duas cópias
     da mesma conta são duas escadas de faixas a divergir no primeiro conserto —
     e esta é a conta que decide se a palavra é «hoje» ou «amanhã». */
  const dias = diasAte(liberaEm ?? '');
  /* `deReserva` vem do SERVIDOR, e o servidor já a escreveu na língua desta
     pessoa (`A8`): não se traduz outra vez. O que se traduz é a reserva da
     reserva — quando nem a frase do servidor veio. */
  if (dias === null) return deReserva ?? t('cadeado.aindaNaoDisponivel');

  const alvo = new Date(liberaEm as string);

  if (dias <= 0) return t('cadeado.abreHoje');
  if (dias === 1) return t('lancamento.abreAmanha');
  if (dias <= 14) return t('lancamento.abreEmDias', { dias });
  return t('cadeado.abreEmData', {
    data: alvo.toLocaleDateString(locale, { day: '2-digit', month: 'long' }),
  });
}

/** Um módulo da página do curso — que agora é uma árvore de dois níveis. */
export interface ModuloDoCurso {
  id: string;
  title: string;
  description: string | null;
  aulas: AulaDaLista[];
  submodulos: ModuloDoCurso[];
}

/**
 * Uma avaliação do curso, do ponto de vista de quem estuda.
 *
 * `melhorNota` é `null` quando a pessoa nunca fechou uma tentativa — e `null`
 * **não é zero**: «não fez» e «tirou 0» são estados diferentes, e a tela diz os
 * dois com palavras diferentes. A nota de corte é a de HOJE, que é a que vale
 * se ela tentar de novo; a que valia em cada tentativa fica congelada no
 * `quiz_attempt`, para o painel do tutor.
 */
export interface AvaliacaoDoCurso {
  aulaId: string;
  titulo: string;
  aprovado: boolean;
  melhorNota: number | null;
  notaMinima: number;
  /** Nulo = sem limite de tentativas. */
  maxTentativas: number | null;
  /** Tentativas FECHADAS. A aberta não conta — ela ainda não tem nota. */
  tentativas: number;
}

/* `extends SinaisDaBarra` desde 08/10/2026: a barra da página do curso saía sem
   as quatro portas, pela mesma causa da página da trilha. */
export interface CursoDetalhado extends SinaisDaBarra {
  curso: {
    id: string;
    title: string;
    subtitle: string | null;
    description: string | null;
    /** Falso = o curso não emite nada, e a tela não oferece o botão. */
    certificateEnabled: boolean;
    /** Nulo = exige 100%. É `RegraDoCertificado`, do core, campo a campo. */
    certificateMinPercent: number | null;
    /** Exige aprovação em toda avaliação publicada do curso. */
    certificateNeedsQuiz: boolean;
    /** A média mínima das melhores notas nas avaliações, em %. Nulo = não exige. */
    certificateMinScore: number | null;
    /**
     * O curso espera outro — e esta é a porta por onde entra quem NÃO passou
     * pela vitrine: o link direto, o favorito, o e-mail de ontem.
     *
     * Com ela a página para de redirecionar para a aula (que responderia 403) e
     * passa a ser um índice. Ver `AvisoDoCursoTrancado`.
     */
    tranca?: TrancaDeOrdem | null;
    /**
     * A data de estreia, para quem chegou por link direto — o favorito, o
     * e-mail de ontem, o link que o tutor mandou antes da hora.
     *
     * Mesmo efeito da tranca de ordem sobre o redirecionamento: a página para
     * de mandar para a aula (que responderia 403) e vira índice. Ver
     * `AvisoDoLancamento`.
     */
    lancamento?: Lancamento | null;
  };
  /**
   * O tema da ÁREA, na página do curso.
   *
   * Faltava, e o efeito era que as cinco cores escolhidas no Estúdio valiam na
   * vitrine e sumiam na sala de aula — onde o aluno passa o tempo. `variaveisDoTema`
   * era chamada sem argumento nas duas telas e caía no padrão de fábrica.
   */
  tema: TemaDaArea;
  /** Só o favicon: o cabeçalho desta página é o título do curso, não da área. */
  marca: { favicon: ImagemDaMarca | null };
  progresso: { percent: number };
  /**
   * O tempo de vídeo do curso e o que esta pessoa já assistiu, em segundos
   * (0439) — a linha da sala: «10 h 42 min de vídeo · você assistiu 3 h 20 min».
   *
   * Irmão de `progresso`, e nunca dentro dele: um conta AULAS e o outro conta
   * SEGUNDOS. Os dois lados andam sobre o mesmo recorte de aulas (publicadas, em
   * módulo à vista, sem simulado), que é o que impede o consumido de passar o
   * total por conta própria.
   *
   * **É só VÍDEO, nos dois lados.** A carga que o tutor fixa à mão conta leitura
   * e exercício e não entra aqui: um curso declarado com 1 h e 12 h de vídeo
   * faria a sala dizer «você já assistiu tudo» aos 27%. A palavra «de vídeo» na
   * tela é o que declara o recorte.
   *
   * `null` = o recurso está desligado para este curso, ou o curso não tem um
   * segundo de vídeo. A tela não desenha, e distingui-los não mudaria um pixel.
   */
  tempo?: { video: number; assistido: number } | null;
  /** Nulo = ainda não emitiu. `revogado` impede oferecer emitir de novo. */
  certificado: { code: string; revogado: boolean } | null;
  /**
   * Uma linha por avaliação publicada do curso, com o veredito desta pessoa,
   * na ordem do curso.
   *
   * Duas leitoras: `podeEmitirCertificado` — a mesma função que a rota de
   * emissão chama — lê `aprovado` e `melhorNota`; o boletim («Seu rendimento»)
   * lê o resto. **Vazia quando o curso não tem avaliação nenhuma**, e é essa a
   * pergunta que a tela faz para decidir se desenha o bloco.
   */
  avaliacoes: AvaliacaoDoCurso[];
  /**
   * A média conjunta das avaliações, do servidor — prova não feita conta 0, que
   * é a mesma regra da régua do certificado. `null` = curso sem avaliação.
   */
  mediaDasAvaliacoes: number | null;
  modulos: ModuloDoCurso[];
  /** A ordem de leitura, calculada pelo servidor — a mesma que numera o sumário. */
  ordem: string[];
  /**
   * O curso seguinte na trilha — o que a última aula deste curso aponta.
   *
   * `null` quando o curso não está em trilha nenhuma, ou é o último dela: aí
   * não há próximo curso, e a sala de aula diz isso em vez de inventar um.
   */
  seguinte: { id: string; title: string; trilha: string } | null;
  /**
   * Onde o curso ABRE ao clicar (04/09/2026): a aula em que a pessoa parou
   * (`retomar`), a próxima de quem concluiu o que tocou (`seguir`), ou a
   * primeira liberada de quem nunca entrou (`comecar`). `null` = nenhuma aula
   * liberada; a lista com os cadeados é o que se mostra.
   */
  abrirEm: { lessonId: string; motivo: 'retomar' | 'seguir' | 'comecar' } | null;
  medicao?: MedicaoDaPagina | null;
}

export interface Aula {
  aula: {
    id: string;
    title: string;
    kind: TipoDeAula;
    /** A espécie (0431): `true` = simulado, treino que não decide nada. Ver
     *  `AulaDaLista.simulado`, que é o mesmo campo na lista. */
    simulado?: boolean;
    body: string | null;
    durationSeconds: number | null;
    temVideo: boolean;
    /** URL de terceiro, já validada contra a allowlist de host do servidor. */
    embedUrl: string | null;
    startsAt: string | null;
    /** O PDF ou o áudio sai pela rota `/arquivo`, nunca por URL neste JSON. */
    temArquivo: boolean;
    content: Record<string, unknown>;
  };
  curso: { id: string; title: string };
  /** Como na página do curso: sem ele, a sala de aula fica no padrão de fábrica. */
  tema: TemaDaArea;
  materiais: {
    id: string;
    kind: 'arquivo' | 'link';
    title: string;
    url: string | null;
    sizeBytes: number | null;
  }[];
  /**
   * ── QUANTAS PERGUNTAS ESTA AULA TEM (02/10/2026) ──────────────────────────
   *
   * O número da aba «Comentários», ao lado do de «Materiais» (que é o tamanho
   * da lista acima). Vem de `contarPerguntasDaAula`, no servidor, com os mesmos
   * cinco termos do `where` do mural — sala, bloqueio, lápide, portão, aula.
   *
   * Opcional porque uma resposta gravada contra a forma anterior não o traz, e
   * ausente lê-se como zero: a aba cala-se, que é como ela sempre esteve.
   */
  comentarios?: number;
  /**
   * O que a sala de aula precisa decidir, e que é escolha do tutor.
   *
   * `proximaAutomatica` é o avanço **entre aulas** — não é o `autoplay` da VSL,
   * que o servidor zera à força para toda aula. `retomar` decide se
   * `progresso.retomarEm` vem preenchido.
   */
  player: { proximaAutomatica: boolean; retomar: boolean };
  /**
   * ── O INTERRUPTOR DO ASSISTENTE, NA ÁREA (§5.5a do desenho) ──────────────
   *
   * `cfg.ia.assistente === 'sim'`, e nada mais: nem teto, nem modelo, nem
   * nome de agente. Um booleano porque a tela tem UMA decisão a tomar com ele
   * — **desenhar ou não desenhar a porta**. Desligado, não há botão apagado,
   * não há cadeado e não há frase: *«uma porta que abre para uma lista vazia é
   * uma porta que ensina a não abrir portas»* (`PortasDaArea.tsx:68`).
   *
   * **Opcional de propósito.** A bancada (`bancada-da-sala.tsx`) e a prévia do
   * Estúdio montam esta mesma sala sem servidor; e um corpo antigo, servido de
   * cache, não tem o campo. Ausente lê-se como **desligado**, que é o lado
   * seguro: a porta que não aparece custa um clique, a porta que aparece sem
   * rota por trás custa a confiança na tela.
   */
  assistente?: boolean;
  /** O limiar da área. A tela precisa dele para saber a resposta antes do servidor. */
  conclusao: { percentual: number };
  progresso: {
    percent: number;
    concluida: boolean;
    secondsWatched: number;
    duracao: number | null;
    /**
     * Onde retomar, já decidido pelo servidor (`posicaoParaRetomar`).
     *
     * Nulo antes de meio minuto e depois de 95%: nos dois extremos a viagem não
     * vale, e um "continuar" que volta ao segundo 4 é pior que não ter.
     */
    retomarEm: number | null;
  };
  /** O que esta pessoa guardou desta aula. */
  marca: { favorito: boolean; assistirDepois: boolean; anotacao: string };
  anterior: { id: string; title: string } | null;
  proxima: { id: string; title: string } | null;
}

/** Uma aula nas listas de favoritos e "assistir depois". */
export interface AulaMarcada {
  id: string;
  cursoId: string;
  title: string;
  kind: TipoDeAula;
  curso: string;
  durationSeconds: number | null;
  /**
   * A capa da aula — os mesmos quatro campos irmãos da vitrine.
   *
   * Vêm do `coverMediaId` da aula (0099), derivados em três tamanhos (0125) e
   * servidos com `srcset`. É o caminho que a prateleira "continue de onde parou"
   * já usa; um segundo caminho de imagem seria uma segunda régua de peso para
   * alguém esquecer de cumprir. `capa` nula = a aula não tem capa, e a tela
   * desenha a marca do curso no lugar (V7/C6).
   */
  capa: string | null;
  capaSrcset: string;
  capaLargura: number | null;
  capaAltura: number | null;
  favorito: boolean;
  assistirDepois: boolean;
  concluida: boolean;
  percent: number;
  /**
   * O CURSO desta aula espera outro (24/09/2026) — e é pelo curso, não pela
   * aula.
   *
   * A fila mostra aulas de cursos vários: perguntar pela dependência de cada
   * aula seria uma ida ao banco por favorito. A dependência da aula em si
   * continua a ser dita dentro da SALA, onde ela vive; aqui a frase nomeia o
   * que tranca o curso daquela aula, que é o que impede o clique.
   *
   * A marca fica: guardar uma aula para depois não depende de a porta estar
   * aberta, e apagar a linha seria apagar o que a pessoa escolheu. O que muda
   * é o convite.
   */
  tranca?: TrancaDeOrdem | null;
  /**
   * O CURSO desta aula **ainda não estreou** (24/09/2026).
   *
   * Aqui a linha FICA, e a decisão é do lado do servidor, escrita na rota:
   * guardar uma aula para depois é um gesto sobre o futuro, e um curso que
   * estreia semana que vem é exatamente o caso em que ele faz sentido. O que
   * muda é o convite — a linha deixa de ser link e diz a data.
   *
   * É o contrário do «continue de onde parou», de onde o curso à espera **sai
   * da lista**: ninguém continua um curso que não começou.
   */
  lancamento?: Lancamento | null;
}

/**
 * Uma trilha PESSOAL do aluno — montada pelo tutor de estudos, ou por ele.
 *
 * Não confundir com `TrilhaNaVitrine`: aquela é do tutor e é de CURSOS. Esta é de
 * AULAS, é de uma pessoa só, e ninguém mais a vê.
 */
export interface TrilhaPessoal {
  id: string;
  titulo: string;
  /** `ia` = montada pelo tutor de estudos; `aluno` = começada por ele. */
  origem: string;
  /** A dúvida que a gerou, nas palavras dele. */
  motivo: string | null;
  criadaEmISO: string;
  etapas: EtapaDaTrilhaPessoal[];
}

export interface EtapaDaTrilhaPessoal {
  ordem: number;
  /**
   * `null` = a aula saiu do ar depois de a trilha ser salva.
   *
   * A etapa FICA, e a tela di-lo: uma lista que encurtasse sozinha faria o aluno
   * acreditar que o caminho dele sempre teve seis passos. O `titulo` continua a
   * ser o que foi lido no instante da montagem — é a cópia que sobrevive à aula.
   */
  aulaId: string | null;
  titulo: string;
  modulo: string | null;
  porque: string | null;
  acrescentadaPeloAluno: boolean;
}

/**
 * As duas listas do aluno — e por que são **duas**, e não uma com um filtro.
 *
 * `favoritos` é *"vou querer reencontrar isto"*: cresce com o tempo e não se
 * esvazia. `assistirDepois` é *"ainda não vi"*: é uma fila, e uma fila que não
 * encolhe é uma fila que a pessoa aprende a ignorar. As duas vêm em campos
 * separados porque uma aula pode estar nas duas ao mesmo tempo — e nesse caso
 * ela aparece **uma vez em cada lista**, nunca duas vezes na mesma.
 */
export interface MinhasAulas {
  tema: TemaDaArea;
  favoritos: AulaMarcada[];
  assistirDepois: AulaMarcada[];
  /**
   * ── AS TRILHAS DELE, A TERCEIRA REGIÃO (30/09/2026) ───────────────────
   *
   * «A MINHA trilha» — com o possessivo, sempre. «Trilhas», sem ele, é a
   * sequência de CURSOS que o tutor monta (`TrilhaNaVitrine`, `area_track`), e as
   * duas convivem na mesma área por escolha do dono: o possessivo é a única coisa
   * que as separa para quem lê, e por isso ele não é opcional em rótulo nenhum.
   *
   * **Opcional** porque um corpo de resposta antigo não a traz, e a tela desenha
   * duas regiões como sempre desenhou.
   */
  trilhas?: TrilhaPessoal[];
  /**
   * As quatro portas da área, para a barra do topo — Agenda · Comunidade ·
   * Minhas aulas · Trilhas.
   *
   * Ordem do dono, 01/10/2026: *«o menu de minhas aulas na barra superior deve
   * ter os mesmos links … como está em agenda»*. Esta tela montava o
   * `Cabecalho` sem `portas`, e a barra saía vazia.
   *
   * **Opcional**, e a ausência desenha a barra na mesma — a mesma escolha da
   * agenda: `temTrilhas` cai em `false` e `comunidadeAberta` em `undefined`, que
   * do lado da porta significa «não sei» e **não esconde**. Uma aba aberta
   * contra um servidor anterior a esta obra vê três portas em vez de nenhuma.
   */
  portas?: PortasDaBarra;
}

/**
 * A marca d'água, já montada pelo servidor.
 *
 * `linhas` chega pronta — a regra de quais campos entram, e a de que marca ativa
 * sem campo nenhum não desenha nada, mora em `linhasDaMarcaDagua`, no core. A
 * tela só desenha o que recebeu, e uma lista vazia significa que não há marca.
 */
export interface MarcaDagua {
  linhas: string[];
  opacidade: number;
  /** Segundos entre um salto de posição e o próximo. */
  intervalo: number;
}

export interface ConfigDoPlayerDaAula {
  config: Record<string, unknown> & { url: string; poster: string | null; duracao: number | null };
  protegido: boolean;
  expiraEm: number | null;
  publicKey: string | null;
  collectorUrl: string;
  identificadores: { externalId: string; email: string } | null;
  /**
   * O veredito do consentimento de gravação de tela, resolvido no SERVIDOR a cada
   * página (`C11` medidor 6, §73.2):
   *
   *  - **`conceder`**: a propriedade grava E este aluno aceitou termos que
   *    **divulgam** a gravação — o «sim»;
   *  - **`negar`**: a propriedade grava e os termos não divulgam — o servidor nega
   *    para apagar um `_vsconsent` que possa ter vazado de outra área (o par
   *    negativo, à prova de cookie);
   *  - **`null`**: a propriedade não grava — não tocamos o consentimento.
   *
   * Nunca sai do cookie `_vsconsent`, sempre do servidor. Ver `ligarMedicao`.
   */
  consentimentoDeGravacao: 'conceder' | 'negar' | null;
  /** O filtro da ÁREA (percentual por aluno, escopo, modo). Ver `lib/medicao.ts`. */
  gravarAqui: boolean;
  /**
   * Os nomes de evento que esta área NÃO registra — `eventos-da-area.ts`.
   *
   * Opcional porque uma aba aberta contra um servidor anterior a esta obra não
   * o recebe, e a ausência tem de significar «mede como sempre».
   */
  eventosBloqueados?: string[];
  marca: MarcaDagua;
  /**
   * ── O ESTADO DA GRAVAÇÃO DE UM ENCONTRO (contrato de 01/10/2026) ─────────
   *
   * Só a porta da **gravação** o manda (`/encontros/:id/gravacao/player`); a
   * porta da **aula** não o conhece, e por isso ele é opcional. `undefined`
   * significa «não há estado a ler», e a tela monta o palco como sempre — é o
   * que mantém toda a sala a funcionar sem uma linha de mudança.
   *
   * | valor | o que o servidor está a dizer |
   * |---|---|
   * | `pronta` | há `config.url`; monte o palco |
   * | `preparando` | o vídeo **existe** e está a converter: `playback` é `null`, e não há palco |
   *
   * Isto substituiu um **404** — e o 404 era uma mentira cara: ele quer dizer
   * «não existe», e o recurso existia. A tela não tinha como distinguir «este
   * encontro não é teu» de «espera dois minutos», e o aluno via um reprodutor
   * preto. Ver `PlayerDaAula`, onde a frase mora.
   */
  estado?: 'preparando' | 'pronta';
}

/**
 * A medição de uma página que NÃO é a sala (vitrine, curso) — as portas de
 * nascimento da gravação fora da sala. `null` quando a área não grava fora
 * dela (o padrão). Ver `lib/medicao.ts`.
 */
export interface MedicaoDaPagina {
  publicKey: string | null;
  collectorUrl: string;
  identificadores: { externalId: string; email: string } | null;
  consentimentoDeGravacao: 'conceder' | 'negar' | null;
  gravarAqui: boolean;
  /** Ver `ConfigDoPlayerDaAula.eventosBloqueados`. */
  eventosBloqueados?: string[];
}

/**
 * Pontos, sequência, selos — e a turma, quando a área a mostra.
 *
 * ## Por que `turma` é `null` na maioria das áreas
 *
 * O placar é **opt-in da área e opt-out do aluno**, e as duas condições valem ao
 * mesmo tempo (`entraNoPlacar`, no core). Nulo significa "esta área não compara",
 * e a tela simplesmente não desenha a linha — não é um estado de erro nem um
 * "carregando".
 *
 * ## O que este contrato NÃO tem, e por quê
 *
 * Não tem posição. "Você é o 187º de 400" informa que 186 pessoas estão à frente,
 * e a leitura é de derrota; a mediana é a mesma informação sem o campeonato. O
 * servidor não devolve a posição para que nenhuma tela possa exibi-la por
 * engano.
 */
export interface MeuProgresso {
  pontos: number;
  /** Dias seguidos de estudo, pelo relógio do servidor. */
  sequencia: number;
  cursosConcluidos: number;
  selos: { slug: string; nome: string; descricao: string }[];
  /**
   * `null` quando `entraNoPlacar` recusa — a área não compara, **ou** a pessoa
   * saiu do placar. *Os dois mundos chegam iguais aqui de propósito.*
   *
   * **`temPlacar` NÃO é `turma >= alguma coisa`** e não se recalcula na tela: é
   * o veredicto de `montarPlacar` no servidor, na janela `sempre` — as 12
   * pessoas com ponto **e** as cinco linhas cheias. *A tela não conhece o piso
   * do placar, e é isso que impede a segunda expressão* (`J932`, `J998`-b).
   */
  turma: { meus: number; mediana: number; turma: number; temPlacar: boolean } | null;
}

/**
 * Um evento da agenda.
 *
 * `startsAt` é o INSTANTE, em ISO com `Z` — nunca uma hora já formatada. Quem
 * converte é a tela, com `formatarNoFuso` do core, para o fuso do aparelho de
 * quem está lendo. O servidor não tem como saber esse fuso sem adivinhar, e as
 * adivinhações (IP, `Accept-Language`, cadastro) erram justamente para quem
 * viaja — que é quem mais precisa que acerte.
 *
 * `timezone` é o fuso em que o tutor MARCOU, e ele viaja junto porque a tela o
 * mostra quando difere do de quem lê: "marcado para 20:00 em America/Sao_Paulo".
 * É a conta à vista, para ninguém ter de confiar na conversão.
 */
export interface EventoDaAgenda {
  id: string;
  title: string;
  description: string | null;
  /** ISO com `Z`. É um instante, e não uma hora de parede. */
  startsAt: string;
  durationMinutes: number;
  timezone: string;
  joinUrl: string | null;
  /** `canceled` VEM na lista: quem esperava precisa saber que não vai haver. */
  status: 'published' | 'canceled';
  courseId: string | null;
  cursoTitulo: string | null;
  /**
   * A gravação e os anexos daquele encontro (`B5`).
   *
   * **Lista vazia, e nunca ausente**: `[]` é «este encontro não deixou nada» e
   * a tela desenha zero secção de mídia; «ainda não sei» não existe aqui,
   * porque a mídia veio na mesma resposta dos eventos. A rota é explícita a
   * respeito, e a tela conta com isso para escolher entre desenhar o bloco e
   * não o desenhar de todo.
   */
  midia: MidiaDoEncontro[];
  /**
   * ── QUANTOS MARCARAM QUE VÃO, E SE EU MARQUEI (contrato de 25/09/2026) ────
   *
   * É **declaração de intenção**, e não inscrição com vaga nem registo de
   * presença: ninguém esteve num encontro por ter carregado num botão dias
   * antes. Nenhuma frase da tela do aluno pode dizer o contrário — ver
   * `CartaoDoEvento`, onde o vocabulário inteiro é «marcar que vai».
   *
   * **Os dois são opcionais no TIPO e obrigatórios no contrato**, e a razão é de
   * versão: uma aba aberta desde antes da subida recebe um corpo sem estes
   * campos, e `undefined` tem de continuar a ser uma resposta possível para a
   * tela — é ela que decide não desenhar o bloco da presença em vez de desenhar
   * um bloco que afirma `0` sobre um servidor que não sabe contar ainda.
   *
   * **`confirmados: null` não é `0`.** `0` é «ninguém marcou ainda» e `null` é
   * «a contagem não veio» — duas telas diferentes, e a que mente é a que as
   * junta. **A tela NUNCA soma**: o número que ela desenha vem do servidor nas
   * duas pontas (na lista e na resposta do gesto).
   */
  confirmados?: number | null;
  euConfirmei?: boolean;
  /**
   * ── A CAPA DO ENCONTRO (contrato de 30/09/2026) ──────────────────────────
   *
   * A mesma forma da capa de um curso na vitrine (`CursoDaVitrine.capa`), e de
   * propósito: **não há uma segunda gramática de capa neste produto.** O
   * original assinado, os degraus do `srcset` e o tamanho real, para o `<img>`
   * reservar a caixa antes de a imagem chegar.
   *
   * `null` é «este encontro não tem capa», e é um estado desenhado — a **placa
   * da data** (`CapaDoEncontro`), que não é uma capa falsa: é a única coisa que
   * se sabe deste encontro, escrita em corpo de cartaz.
   *
   * **Todos opcionais**, e a razão é de versão: uma aba aberta desde antes da
   * subida recebe um corpo sem estes campos, e a agenda tem de continuar a
   * desenhar-se — sem capa, no piso da casa.
   */
  capa?: string | null;
  capaSrcset?: string;
  capaLargura?: number | null;
  capaAltura?: number | null;
  /**
   * O que ESTE encontro escolheu para a capa dele. `null` = não mexi.
   *
   * A lei é a de `prateleira-aparencia.ts`, aplicada a um terceiro degrau
   * (área → acervo → encontro): **«não mexi» nunca é «escolhi o mesmo»**. Um
   * encontro que nascesse com uma cópia do valor do acervo ficaria para trás no
   * dia em que o tutor mudasse o acervo, e ninguém conseguiria distinguir a
   * escolha dele do eco de um padrão antigo. Ver `aparenciaDoEncontro`.
   */
  capaProporcao?: ProporcaoDeCapa | null;
  capaTamanho?: TamanhoDeCapa | null;
  /**
   * ── A GRAVAÇÃO É UMA AULA (contrato de 30/09/2026) ───────────────────────
   *
   * `area_event.recording_lesson_id`. Com ela, a janela do acervo monta
   * `PlayerDaAula` — o MESMO componente da sala, com pôster, marca d'água,
   * retomada e o pipeline de legenda do V-Poliglota, que tem a porta presa a
   * `:aulaId`.
   *
   * **Ela só vem quando esta pessoa alcança a aula**, e não quando ela existe:
   * o acervo respeita `access_group`, e quem não vê o encontro não vê a
   * gravação. `null` com `midia` cheia é o mundo anterior — o ficheiro cru, que
   * se baixa; `null` com `midia` vazia é «ainda não subiu».
   *
   * **Em caminho de saída desde 01/10/2026** — ver `gravacao`, abaixo. Fica
   * enquanto o servidor o mandar: tirá-lo no mesmo instante em que o campo novo
   * nasce deixaria toda aba aberta sem gravação nenhuma.
   */
  gravacaoAulaId?: string | null;
  /**
   * ════════════════════════════════════════════════════════════════════════
   * ── A GRAVAÇÃO É UM VÍDEO DO EVENTO, E NÃO UMA AULA (01/10/2026) ────────
   * ════════════════════════════════════════════════════════════════════════
   *
   * O contrato de 30/09 dizia que a gravação **era** uma aula
   * (`recording_lesson_id`), e isso era um erro de conceito: *um encontro não é
   * uma aula de curso nenhum.* A gravação é um vídeo do próprio evento,
   * processado pelo mesmo pipeline dos vídeos de aula — e por isso abre no
   * **player da casa** —, mas não tem prateleira, não tem ordem num módulo e
   * não conta progresso de curso.
   *
   * | campo | o que ele é | o que a tela faz com ele |
   * |---|---|---|
   * | `playerVideoId` | o vídeo no runtime do player | monta o palco |
   * | `duracaoSegundos` | a duração MEDIDA do ficheiro | carimba a capa — e `null` **cala**, nunca vira `0:00` |
   * | `temLegenda` | o V-Poliglota já legendou? | a marca `cc` ao lado da duração |
   *
   * **`null` é «ainda não há gravação»**, e não «não há»: o tutor sobe-a
   * depois. **`undefined` é outra coisa** — é o servidor que ainda não fala
   * deste campo —, e por isso a tela lê os dois contratos ao mesmo tempo (ver
   * `videoDaGravacao`, em `agenda/gravacao-do-encontro.ts`).
   *
   * E o que ela SUBSTITUI é um download: até 01/10/2026 a janela de um encontro
   * sem aula oferecia *«Baixar a gravação — esta gravação é um ficheiro, e abre
   * fora daqui»*. **Isso deixou de ser o produto, por ordem do dono.**
   */
  gravacao?: {
    playerVideoId: string;
    /** `null` = a duração não foi medida. Não se carimba nada. */
    duracaoSegundos: number | null;
    temLegenda: boolean;
  } | null;
}

/**
 * ── A ESTREIA DE UM CURSO, COMO A AGENDA A RECEBE (30/09/2026) ─────────────
 *
 * **O tipo é o do CORE**, e não uma segunda declaração — a mesma lei de
 * `Lancamento`: uma cópia aqui é exactamente o que faz as duas pontas
 * divergirem um campo de cada vez. A régua que diz o que é uma estreia, em que
 * dia ela cai e que palavra a nomeia mora em `@vestigo/core/area-estreia-na-agenda`,
 * e é lida pela agenda do aluno e pela do tutor.
 *
 * Ela viaja numa lista **própria** (`estreias`), ao lado de `eventos`, e nunca
 * misturada com eles: um encontro tem duração, sala, confirmação e gravação, e
 * uma estreia não tem nenhuma das quatro. *A primeira tela que se esquecesse
 * disso desenharia «Entrar na sala» sobre um curso.*
 */
export type { EstreiaDeCurso as EstreiaDaAgenda } from '@vestigo/core/area-estreia-na-agenda';

/**
 * Um arquivo pendurado num encontro — a gravação da live, ou o material.
 *
 * `url` é assinada na hora pelo servidor e **vence**: não se guarda, não se
 * repõe num estado, não se manda para lado nenhum. `null` quando a assinatura
 * falhou — e uma linha sem link não se desenha, que é a escolha do servidor
 * escrita também aqui: um arquivo a menos na tela, nunca a agenda inteira sem
 * abrir.
 */
export interface MidiaDoEncontro {
  assetId: string;
  especie: 'gravacao' | 'anexo';
  ordem: number;
  /** O nome que a pessoa escolheu no computador dela. `null` nas linhas velhas. */
  nome: string | null;
  contentType: string;
  sizeBytes: number | null;
  /** Só nas gravações, e só quando o processamento a mediu. */
  duracaoMs: number | null;
  url: string | null;
}

/**
 * ── A AULA DE UM POST (`AUL`) ───────────────────────────────────────────────
 *
 * O que o servidor manda para a tela poder desenhar a referência da aula sem
 * uma segunda ida: o quadro, o nome do curso, onde ela mora e quanto dura.
 *
 * ## O NÚMERO vem PARTIDO, e continua a ser o do TUTOR
 *
 * `numero` e `curto` saem de `etiquetaDaAula` — a função do core —, chamada uma
 * vez no servidor. A lei não muda: *o número da aula é o que o tutor escreveu no
 * título, nunca um ordinal nosso*. O que muda é onde a função corre, e a razão é
 * o telemóvel: ele não pode importar `area-comunidade` (`SI1`), e a alternativa
 * seria ele reescrever o corte do prefixo à mão — a segunda régua que produziria
 * «Aula Aula 02» num sítio onde ninguém mediu.
 */
export interface AulaDoPost {
  id: string;
  /** O título como o tutor o escreveu, inteiro. */
  titulo: string;
  /** O número que ele escreveu no título, ou `null` quando não numerou. */
  numero: number | null;
  /** O título sem o prefixo «Aula NN» — e o título inteiro quando não sobra nome. */
  curto: string;
  /** `video` | `texto` | `pdf` | `audio` | `embed` | `live` | `quiz` | … */
  especie: string;
  /** `null` quando ninguém mediu — e `null` não se desenha como «0 min». */
  duracaoSegundos: number | null;
  cursoId: string;
  cursoTitulo: string;
  /** `null` num curso de módulo único sem nome próprio. */
  moduloTitulo: string | null;
  /** A miniatura da aula, ou a capa do curso. `null` quando não há nenhuma. */
  capa: string | null;
  capaSrcset: string;
}

/** Um post no mural. `removido` só é verdadeiro para o próprio autor. */
/** O `VideoReconhecido` do core (`midia/provedores-de-video.ts`), como viaja. */
export interface VideoDoPost {
  provedor: string;
  nome: string;
  id: string;
  embed: string;
  publico: string;
  miniatura: string | null;
}

export interface PostDoMural {
  id: string;
  title: string | null;
  body: string;
  /**
   * O corpo já em HTML, feito no servidor ao publicar — **e `null` é um estado
   * de primeira classe, não um erro.**
   *
   * `null` é o que os posts anteriores a 09/09/2026 têm, e o que faz o leitor
   * cair no caminho de sempre: `body` cru dentro de um nó `whitespace-pre-wrap`.
   * Nenhuma linha antiga foi reescrita para ganhar esta coluna.
   *
   * Quando existe, é a saída de `renderMarkdown` — o renderizador da casa, com
   * a lista dos sete nós. É o único valor deste app que vai a um `__html` de um
   * post, e é o que faz o mural ficar rico **sem que quem só lê carregue um
   * renderizador de markdown**: +0,2 kB, contra os +14,6 kB do outro caminho.
   */
  bodyHtml: string | null;
  /**
   * ── A CAPA DO POST (contrato §7) ──────────────────────────────────────────
   *
   * **`null` é o estado normal e não é um erro** — é o que todo post anterior a
   * esta leva tem, e o que um post só de texto tem para sempre. O cartão sem
   * capa desenha-se exatamente como se desenhava; nenhuma moldura vazia, nenhum
   * cinzento à espera de imagem.
   *
   * Ela carrega o **id do asset**, nunca a URL: o endereço monta-se em
   * `enderecoDaMidiaDoMural`, que aponta para o proxy. `alt` é o que a pessoa
   * escreveu, e `null` quando ela não escreveu nada — a capa é decorativa nesse
   * caso e sai do fluxo do leitor de tela, em vez de ganhar um texto inventado.
   */
  capa?: {
    assetId: string;
    alt: string | null;
    kind: 'imagem' | 'video';
    /**
     * `escolhida`, e só — a derivação da primeira imagem do corpo foi derrubada
     * por ordem do dono de 17/09/2026 («o sistema não escolhe capa»). O campo
     * fica porque a resposta o traz e o app do telemóvel o lê.
     */
    origem?: 'escolhida';
  } | null;
  /**
   * O vídeo do post, já entendido pelo servidor com a lista de provedores do
   * core — o do corpo, ou a coluna `url` dos posts antigos de `tipo = 'video'`.
   * O `embed` e a `miniatura` vêm montados pela casa: a tela não lê endereço
   * nenhum para decidir o que é vídeo.
   */
  video?: VideoDoPost | null;
  authorKind: 'member' | 'operator';
  authorMemberId: string | null;
  autor: string;
  courseId: string | null;
  lessonId: string | null;
  cursoTitulo: string | null;
  aulaTitulo: string | null;
  /**
   * ── A AULA DE ONDE A PERGUNTA VEIO, COM CARA (`AUL`) ────────────────────
   *
   * `null` na esmagadora maioria — o post que nasceu no mural não tem aula —, e
   * `null` também na lápide. Não é `aulaTitulo` com outro nome: aquele é um
   * texto de 16ch ao lado do autor, este é a **referência visual** que diz de
   * que aula se fala e leva de volta a ela.
   */
  aula: AulaDoPost | null;
  pinnedAt: string | null;
  removedAt: string | null;
  removedReason: string | null;
  createdAt: string;
  /** A SALA. Nulo = post anterior às salas, ou área que ainda não tem nenhuma. */
  espacoId: string | null;
  espacoSlug: string | null;
  espacoTitulo: string | null;
  tipo: 'pergunta' | 'conversa' | 'aviso' | 'conquista' | 'artigo' | 'link' | 'video';
  /**
   * ── O ENDEREÇO DE `link` E `video` (`Q6`, 0344) ────────────────────────
   *
   * `null` em todos os outros tipos, e o `area_post_url_ck` do banco garante a
   * implicação nos dois sentidos. **A tela desenha o cartão pelo TIPO, nunca
   * pela presença desta chave** — senão um `conversa` com endereço gravado por
   * engano ganharia desenho de `link` sem ninguém ter decidido isso.
   *
   * Já vem normalizado pelo servidor (`enderecoDoPost`, lista BRANCA de `http`
   * e `https`): o que chega aqui é seguro de pôr num `href`.
   */
  url: string | null;
  /**
   * ── O POSTAL (`I1` `I3`) — e ele NUNCA sai do corpo do post ───────────────
   *
   * O que a faixa do troféu diz vem de `area_conquista`, congelado no instante
   * do fato. **Se a pessoa escrever «tirei 100» e a nota gravada for 62, a
   * medalha ao lado diz 62** — o `body` é dela, isto não é, e a tela não tem por
   * onde ler o outro número.
   *
   * `null` em todo post que não é troféu, que é a esmagadora maioria.
   */
  conquista: { genero: GeneroDaConquista; payload: PayloadDaConquista } | null;
  /**
   * A contagem que o ALUNO consegue abrir — vinda da coluna materializada, e
   * não mais de uma subconsulta por linha. O nome no fio continua `comentarios`
   * porque é o que a tela lê; o que mudou é de onde ele sai.
   */
  comentarios: number;
  /**
   * ── A SÉTIMA COISA DO CARTÃO (`Q5`, 0340) ─────────────────────────────────
   *
   * A última resposta que ESTA pessoa consegue abrir — **não é `createdAt`**.
   * As duas datas ficam na mesma linha do cartão de propósito: uma diz quando
   * a pergunta foi feita, a outra diz se ela ainda está viva. Um post de 8
   * dias com resposta de 3 h parece morto quando só a primeira aparece.
   *
   * `null` = nenhuma resposta publicada. Não é «há 0 h», e a tela lê os dois
   * de forma diferente.
   */
  lastCommentAt: string | null;
  /**
   * Até TRÊS rostos de quem participou (`Q5`, valor 6), do mais recente para o
   * mais antigo. A contagem por extenso continua a ser `comentarios`: quatro
   * comentaristas desenham **três** rostos e escrevem **«4 respostas»**.
   */
  participantes: RostoNaLinha[];
  /** O VOTO, que substituiu a reação. Zero pontos, por decisão. */
  votos: number;
  votantes: number;
  votei: boolean;
  calor: number;
  /** Verdadeiro = a lápide. O corpo vem vazio; o que se lê é o motivo. */
  removido: boolean;
  /**
   * `AUL`: quem escreveu esta pergunta é quem está a olhar.
   *
   * É o que abre «Editar» e «Apagar» numa LISTA — a página do post já tinha o
   * `souOAutor` dela, e as listas não tinham nada. Opcional para as respostas
   * que já estavam escritas contra esta forma; ausente lê-se como «não é meu»,
   * que é o estado seguro: esconde um gesto, nunca oferece um 403.
   */
  souEu?: boolean;
  /**
   * ── ONDE ESTE POST ESTÁ NO PORTÃO (`Q9`, 0347) ───────────────────────────
   *
   * `em_revisao` **só chega a quem escreveu** — o `where` do servidor não o
   * traz para mais ninguém, e `visaoDoPost` devolve `oculto` a terceiros. A
   * tela pode por isso desenhar a marca sem perguntar de quem é o post.
   *
   * **Não é `removido`.** Aquele foi dito a alguém e a moderação tirou, com um
   * motivo que o autor lê na lápide; este ainda não foi dito a ninguém.
   */
  moderacao: 'aprovado' | 'em_revisao';
  /**
   * ── A MARCA DE EDITADO (`Q8`, 0348) ───────────────────────────────────────
   *
   * Quando o AUTOR reescreveu o texto. Nulo é a maioria, e nulo não é «há 0 s».
   * **Não é `updatedAt`**, que se move por fixar, remover e restaurar — uma
   * marca de edição feita dele diria «editado» a todo post que o tutor fixou.
   */
  editedAt: string | null;
  /** O ROSTO de quem escreveu, montado no servidor. Ver `RostoNaLinha`. */
  rosto: RostoNaLinha;
  /**
   * ── O FIO (`Q11`) — as ÚLTIMAS respostas, até `RESPOSTAS_NO_FIO` ──────────
   *
   * *«ele não precisa ter que entrar em uma postagem para poder ver
   * comentarios»*. O que chega é o **fim** da conversa, da mais velha para a
   * mais nova — a inversão é do servidor, para não haver duas ordens.
   *
   * **Lista vazia quer dizer «não há fio»**, e não «desenhe vazio»: o cartão sem
   * resposta nenhuma não ganha controle nenhum (§1, `D3`). A lápide também
   * chega vazia.
   *
   * **Quantas ficaram de fora não viaja aqui**: sai de `respostasDeFora`, no
   * core, cruzando `comentarios` (a coluna materializada, que é o número do
   * cartão) com o tamanho desta lista. Um terceiro número no fio seria uma
   * terceira coisa a ficar diferente das outras duas.
   */
  respostas: RespostaNoFio[];
}

/**
 * Uma resposta como o FIO do mural a mostra — e ela é MENOR que
 * `ComentarioDoMural` de propósito.
 *
 * O fio lê; a página do post age. Não há `removido` (o fio nunca mostra lápide
 * — ver `respostasDoFio` no servidor) e não há `authorMemberId`: nenhum
 * controle do fio precisa dele. **`souEu` passou a viajar** (`J1261` item 5):
 * o coração do fio é um controle, e ele não pode ser oferecido sobre a própria
 * resposta — o servidor recusa-o com 403. *«Pedir o que não se
 * usa é o que impede a mesma peça de servir a segunda tela»* — e o inverso
 * também é verdade: um campo que chega e ninguém lê é o próximo a ser lido pela
 * razão errada.
 *
 * `body` chega **cortado** em 480 caracteres pelo servidor. O fio corta a duas
 * linhas de CSS muito antes disso; quem quer o texto inteiro abre a conversa.
 */
export interface RespostaNoFio {
  id: string;
  body: string;
  authorKind: 'member' | 'operator';
  createdAt: string;
  editedAt: string | null;
  /** Marcada por quem perguntou. O único fato da comunidade que dá ponto. */
  helpfulAt: string | null;
  /**
   * ── O CURTIR DA RESPOSTA, TAMBÉM NO FIO (`CO12` + `AUL`) ─────────────────
   *
   * A rota nasceu na 0363 e só a página do post a desenhava — e o fio é onde a
   * esmagadora maioria das respostas é lida. *«Outros alunos podem curtir os
   * comentários e respostas ao comentário»*, e agora podem onde elas estão.
   *
   * Opcionais para não partir o que já estava escrito: um fio vindo de uma
   * resposta antiga da rede lê-se como zero, e zero é o estado verdadeiro de
   * quem ainda não recebeu coração nenhum.
   */
  votos?: number;
  votei?: boolean;
  /**
   * A resposta é de quem está a olhar (`J1261` item 5): o coração não se
   * oferece — o servidor recusa curtir a própria resposta com 403. Ausente
   * lê-se «não é minha», e o servidor manda-o sempre.
   */
  souEu?: boolean;
  rosto: RostoNaLinha;
}

/**
 * O rosto de quem escreveu — o que o `left join` com `member_perfil` traz ao
 * mural e ao post aberto.
 *
 * `nome` é sempre o do cadastro: o apelido é acréscimo e aparece **ao lado**,
 * nunca no lugar. `arroba` e `avatar` são nulos para a maioria das pessoas, e
 * isso não é um estado degradado — é quem ainda não abriu o perfil.
 */
export interface RostoNaLinha {
  nome: string;
  /** Já com a arroba (`@ana.dev`), pronto para a tela. */
  arroba: string | null;
  /** URL assinada, ou `null` — a tela cai na inicial, que é a verdade. */
  avatar: string | null;
  /**
   * O nível de quem escreveu (`H3`), **só no mundo nominal** — a área compara,
   * quem lê está no placar e quem escreveu também. Ausente ou `null` = não há
   * número, e a ficha não pinta nada (`J704`-b). A tela nunca o calcula.
   */
  nivel?: NivelDaPessoa | null;
}

/**
 * `GET /a/:areaSlug/comunidade/nivel` — o nível de quem lê e onde a turma está.
 *
 * - `eu` é o nível de quem lê, **sempre** — o próprio vê sempre o seu;
 * - `visivelAosOutros` é a MESMA expressão que decide a ficha nos cartões
 *   (`entraNoPlacar`): a frase sobre quem vê o nível sai daqui, e não de um
 *   palpite da tela;
 * - `turma` é a distribuição sem nomes, ou `placar` quando a comparação desta
 *   pessoa é a nominal — as duas nunca dividem a tela.
 */
export interface NivelNaArea {
  eu: NivelDaPessoa;
  visivelAosOutros: boolean;
  turma: DistribuicaoPorNivel | { estado: 'placar' };
}

/** Uma sala da comunidade, como o aluno a vê — com a VIDA dela (`D4`). */
export interface EspacoDoMural {
  id: string;
  slug: string;
  titulo: string;
  descricao: string | null;
  /**
   * Quantos posts esta pessoa alcança nesta sala. **A tela nunca desenha este
   * número quando ele é zero** — sala sem post mostra «seja o primeiro», e não
   * um `0` cinzento. Ele existe para distinguir os dois estados, não para ser
   * mostrado.
   */
  posts: number;
  /** O sinal de vida: quando alguém escreveu aqui pela última vez. */
  ultimoEm: string | null;
  /**
   * O que a sala VAZIA diz, e o que o compositor diz antes de a pessoa escrever
   * — **resolvidos no servidor desde `D6`**.
   *
   * Eram derivados na tela, de `espacoPorSlug(slug)`, e isso funcionava enquanto
   * todo slug estava numa lista de cinco. Com o tópico livre a mesma chamada
   * devolve `null` para a sala que o tutor acabou de criar — e ela abriria com
   * a frase de outra sala ou com nenhuma, que é o §1 da régua a acontecer
   * dentro da leva que o veio consertar.
   */
  convite: string;
  marcador: string;
}

/**
 * ── O HERÓI DA COMUNIDADE (`D6`) — JÁ RESOLVIDO, e por isso tão pequeno ─────
 *
 * `null` quer dizer **não desenhe nada**, e nunca «desenhe vazio»: o tutor não
 * ligou o herói, e um herói genérico no topo da comunidade é pior do que
 * nenhum. Mesma leitura de `aulas`.
 *
 * As cores chegam **decididas**: a sobrescrita da comunidade, ou o tema da
 * área. A tela não conhece a regra de herança — o painel do tutor desenha a
 * mesma prévia a partir da mesma decisão, e duas leituras da mesma herança
 * seriam duas telas a discordar sobre uma cor que o tutor escolheu uma vez.
 */
export interface HeroiDaComunidade {
  titulo: string;
  chamada: string;
  /** URL assinada, ou `null`. Nunca um id: assinar é do servidor. */
  imagem: string | null;
  veu: number;
  /** O corte do tutor (21/09): ponto focal em % e zoom. Ausente/`null` = o centro. */
  enquadramento?: { x: number; y: number; zoom: number } | null;
  /** O corte abaixo de 640 px; ausente/`null` = o da tela. */
  enquadramentoCelular?: { x: number; y: number; zoom: number } | null;
  /**
   * A proporção já resolvida — `'3 / 1'`, como o CSS a escreve. O nome do
   * degrau (`baixa`/`media`/`alta`) fica no servidor: `heroi-geometria.ts` é o
   * dono da tabela, e uma segunda cópia dela numa tela é a divergência que
   * `razaoDoAspecto` proibe por escrito.
   */
  aspecto: { celular: string; tela: string };
  cores: { fundo: string; superficie: string; destaque: string; texto: string };
  raio: number;
}

/** A ordem que de facto valeu, e a razão quando ela não foi a pedida. */
export interface OrdemDoMural {
  ordem: 'novos' | 'em_alta' | 'melhores';
  pedida: 'novos' | 'em_alta' | 'melhores';
  caiu: boolean;
  porque: string | null;
}

/**
 * ── UMA PERGUNTA MINHA COM RESPOSTA NOVA (`PER`) ─────────────────────────────
 *
 * O sinal com destinatário. **Uma linha por FIO, e é a resposta mais nova** —
 * duas respostas no mesmo post são um regresso, não dois.
 *
 * Não há campo `total`: a contagem é o tamanho da lista. Um número ao lado das
 * linhas seria uma segunda expressão da mesma coisa, e a que ficasse diferente
 * seria a que diz «3» sobre duas linhas.
 */
export interface RespostaParaMim {
  postId: string;
  titulo: string | null;
  /** O corpo cortado — é o que dá nome ao fio quando o post não tem título. */
  trecho: string;
  quem: string;
  arroba: string | null;
  quando: string;
}

/** O crachá da porta: o número e a frase que diz o que ele conta (`J1299` c6). */
export interface CrachaDaComunidade {
  quantos: number;
  rotulo: string;
}

/**
 * ── UM AVISO DIRIGIDO QUE NÃO É RESPOSTA (`J1299`) ─────────────────────────
 *
 * A menção numa resposta, ou a minha resposta marcada como a que resolveu. O
 * trecho é do COMENTÁRIO (onde está a menção, ou a resposta que foi marcada), e
 * o título é do post.
 *
 * **`ponto` só existe em `resposta_marcada`, e é LIDO do razão pelo servidor**
 * — a tela não calcula ponto nenhum, nem sabe quanto vale uma resposta útil:
 *
 * - `{ pontos }` — o número da linha de `member_point` desta resposta;
 * - `{ limiteDoDia }` — o dia (`AAAA-MM-DD`, o da MARCAÇÃO, e nunca «hoje»);
 * - `null` — não se diz nada sobre pontos, só o gesto.
 */
export interface AvisoParaMim {
  id: string;
  tipo: 'mencao' | 'resposta_marcada';
  postId: string;
  /** `null` quando o texto que menciona é o próprio post (`J1300`). */
  comentarioId: string | null;
  titulo: string | null;
  trecho: string;
  quem: string;
  arroba: string | null;
  quando: string;
  ponto: { pontos: number } | { limiteDoDia: string } | null;
}

export interface MuralDoAluno {
  tema: TemaDaArea;
  /** `D6`. `null` = o tutor não ligou o herói — não desenhe nada. */
  heroi: HeroiDaComunidade | null;
  posts: PostDoMural[];
  /**
   * ── AS RESPOSTAS QUE SÃO PARA MIM (`PER-03`) ──────────────────────────────
   *
   * **Lista vazia = não desenhe nada** (`PER-04`), e é o estado normal. Ausente
   * num corpo antigo pela mesma razão.
   */
  respostasParaMim?: RespostaParaMim[];
  /** `J1299`: a menção e a resposta marcada. Ausente num corpo antigo = nada. */
  avisosParaMim?: AvisoParaMim[];
  temMais: boolean;
  pagina: number;
  espacos: EspacoDoMural[];
  espaco: string | null;
  /**
   * ── A ESPINHA (`D5`) — as aulas que TÊM post nesta área ────────────────────
   *
   * **Lista vazia quer dizer «não desenhe nada».** O servidor já aplicou o piso
   * de DOIS (`espinhaDaArea`, `PISO_DA_ESPINHA`) e a visibilidade herdada do
   * curso (`podeVerCurso`) — a tela não recalcula nem uma coisa nem outra, e é
   * essa a razão de o piso viver numa função pura do core: dois pisos são um
   * piso que diverge.
   *
   * `numero` é `null` quando o TUTOR não numerou a aula, e nesse caso o nó
   * desenha-se sem algarismo. **Nunca «Aula —»** (`J702`-7).
   */
  aulas: GrupoDaEspinha[];
  /**
   * A última aula que ESTA pessoa tocou nesta área, ou null. Um id, e mais
   * nada: quem decide se «Da minha aula» se desenha é a tela, cruzando-o com
   * a espinha. Se a minha aula não tiver post, o chip não existe — um filtro
   * que devolve lista vazia é um controle sem produto.
   */
  minhaAula: string | null;
  /**
   * ── OS DOIS PORTÕES DA ÁREA (`Q9`) ────────────────────────────────────────
   *
   * **É um limite, e um limite vai escrito na tela.** Quem publica numa área
   * com «eu aprovo antes» tem de o saber **antes** de escrever: sem isso, a
   * pessoa carrega em «Publicar», não vê o texto no mural e conclui que o site
   * falhou — e escreve outra vez.
   *
   * Os dois viajam porque são dois eixos independentes: o compositor lê o
   * primeiro, a caixa de responder lê o segundo.
   */
  portao: PortaoDaComunidade;
  ordem: OrdemDoMural;
  /** `datas` é o rótulo por extenso — «5 AGO – 5 SET». `Q2` exige que apareça. */
  periodo: { valor: string; datas: string };
  /**
   * O rosto de quem está a olhar. Nasceu para a linha do compositor (`D4`); a
   * partir de `Q11` é também o rosto da resposta que a pessoa escreve DE DENTRO
   * do mural — a que cresce no fio sem a página recarregar.
   */
  eu: { nome: string | null; avatar: string | null; arroba: string | null };
  /**
   * ── OS TETOS DA MÍDIA, PARA IREM À TELA ANTES DO ERRO (contrato §6) ───────
   *
   * *«o que não é negociável é ele estar escrito na tela ANTES de a pessoa
   * tentar, e a recusa ser a frase do teto e não um 500»*. Quem sabe o teto é o
   * servidor — ele muda com o provedor de vídeo da área —, e por isso ele
   * viaja com o mural, que é a chamada que já existe e que o compositor já
   * espera.
   *
   * **Opcional, e a ausência é silêncio**: enquanto a rota não o mandar, o
   * rodapé da caixa não inventa um número. *Uma tela que escreve um teto que o
   * servidor não confirmou é a tela a prometer o que o código não faz.*
   */
  midia?: TetosDaMidiaDoMural;
  /**
   * ── A SALA QUE O TUTOR MARCOU COMO SALA DAS AULAS (16/09/2026) ────────────
   *
   * O slug dela, e **só quando ela foi MARCADA** — nunca a primeira sala por
   * omissão, que é o degrau de reserva de `salaDasPerguntasDaAula`. É o que a
   * ordem do dono pede: *«só devem aparecer as aulas para selecionar se a sala
   * de dúvidas foi marcada»*.
   *
   * `null` = o tutor não marcou nenhuma. **Ausente = o servidor ainda não o
   * manda**, e a tela lê as duas da mesma maneira: não desenha o seletor. A
   * omissão segura é a que não oferece o que o servidor recusa.
   */
  salaDasAulas?: string | null;
}

/** O que cada espécie de arquivo pode ser, dito pelo servidor (contrato §6). */
export interface TetoDeUmaMidia {
  /** O teto em bytes, para a tela recusar antes de gastar a rede da pessoa. */
  tetoBytes: number;
  /** O teto POR EXTENSO, que é o que vai na tela: «até 8 MB». */
  tetoEscrito: string;
  /** Os tipos aceites, como o `accept` de um seletor de arquivo. */
  mimes: string[];
}

export interface TetosDaMidiaDoMural {
  imagem: TetoDeUmaMidia;
  /** `null` = esta área não aceita vídeo enviado (só o colado de provedor). */
  video: TetoDeUmaMidia | null;
}

/**
 * ── O ENDEREÇO DE UMA MÍDIA DO MURAL — ESTÁVEL, E NUNCA ASSINADO ────────────
 *
 * É o proxy do contrato §3: sem slug no caminho (slug muda, e o HTML
 * materializado não pode depender dele) e sem assinatura (uma URL assinada
 * gravada vence em seis horas e o post fica sem imagem numa tarde — é a
 * cicatriz de `capas-da-area.ts`). Quem autoriza é a sessão mais a posse do
 * asset, e o asset de outra área responde **igual** ao inexistente.
 */
export function enderecoDaMidiaDoMural(assetId: string): string {
  return `${BASE}/midia-do-mural/${encodeURIComponent(assetId)}`;
}

/*
 * O ENVIO de um arquivo do mural (bilhete, depósito, `completar`) mudou-se para
 * `@vestigo/compositor-de-post/casa` (`enviarPorBilhete`, 16/09/2026): o painel
 * monta o mesmo compositor e envia pelos mesmos três passos, com o cliente
 * dele. Esta app passa os seus dois pedidos em `comunidade/casa-do-aluno.ts` —
 * e o pedaço de entrada deixou de carregar um envio que só o compositor usa.
 */

/**
 * ── QUEM ESTÁ (`B3`) — a coluna da direita ──────────────────────────────────
 *
 * A CONTAGEM, e nunca a lista. Uma contagem não tem nome e não tem
 * `ranking_opt_out`; *último-visto por pessoa é vigilância, agregado é
 * presença, e a linha entre as duas é o nome*. É por isso que `chegaram.rostos`
 * carrega **inicial e foto, nunca nome** — o que não viaja não é desenhado por
 * engano depois.
 *
 * E é rota à parte de propósito: a resposta não muda com o filtro, com a ordem
 * nem com a página, e o mural é a superfície de leitura mais quente da área.
 */
/** Os dois interruptores da área, como o servidor os devolve (`Q9`). */
export interface PortaoDaComunidade {
  posts: 'livre' | 'aprovacao';
  comentarios: 'livre' | 'aprovacao';
}

/**
 * ── O PLACAR DA ÁREA (`H8`) — e o que este contrato NÃO tem ─────────────────
 *
 * **Não tem contagem de turma.** Nem `turma`, nem `total`, nem «5 de 48». O
 * placar exclui quem saiu por `ranking_opt_out`, e uma contagem ao lado de uma
 * lista que exclui é **uma subtração desenhada na tela** — numa turma pequena,
 * com nome. A resposta é a mesma numa turma de 12 e numa de 400, e há cerca a
 * afirmá-lo dos dois lados do fio.
 *
 * **Não tem posição absoluta.** `posicao` é `1..5` **dentro da lista**; quem não
 * está entre os cinco não recebe linha nenhuma. *«Você é o 187.º»* publica o
 * denominador tanto quanto escrevê-lo.
 *
 * **Não tem identificador de pessoa.** Quem diz qual das cinco linhas é a de
 * quem lê é o `eu` — um id no fio é um nome no fio assim que alguém tiver duas
 * contas.
 *
 * Os quatro estados são exaustivos, e cada um é uma tela diferente: a área que
 * não compara desenha **nada**; quem saiu lê **que saiu**; a turma pequena lê
 * **o porquê**; e `ok` traz as cinco.
 */
export type PlacarDaArea =
  | { estado: 'area-nao-compara'; periodo: PeriodoDoPlacar }
  | { estado: 'voce-saiu'; periodo: PeriodoDoPlacar }
  /*
   * `temTemporada` só vem nos dois estados que DESENHAM abas. Ele não é uma
   * contagem da turma: é sobre a ÁREA, e o tutor já a anunciou ao marcá-la.
   * *Sem ele, a aba «Temporada» apareceria onde não há nenhuma — um corte que a
   * pessoa clica e não devolve nada.*
   */
  | { estado: 'ainda-nao'; periodo: PeriodoDoPlacar; temTemporada?: boolean }
  | {
      estado: 'ok';
      periodo: PeriodoDoPlacar;
      temTemporada?: boolean;
      linhas: LinhaDoPlacarNaTela[];
    };

export interface LinhaDoPlacarNaTela {
  /** `1..5`, dentro da lista. Nunca a posição na turma. */
  posicao: number;
  nome: string;
  /** Pontos DENTRO da janela pedida — nunca o total de vida. */
  pontos: number;
  eu: boolean;
}

export interface QuemEsta {
  presenca: { naArea: number; online: number; daEquipe: number };
  /**
   * ── A AGENDA (`B4`) — até TRÊS encontros futuros, ou NENHUM ────────────────
   *
   * Lista, e não um objeto que pode ser nulo: **vazia quer dizer «não desenhe o
   * bloco»**, que é o oráculo negativo de `B4`. Uma moldura «Agenda» com um
   * traço à espera é a tela a prometer o que a área não tem — e *medido em
   * 07/09/2026, `area_event` publicados e futuros = 0 em toda a base*, o que
   * faz do vazio o estado real de hoje e não a exceção.
   *
   * Ela LÊ `area_event`; **nunca inventa um segundo sistema de eventos**. Quem
   * escreve a hora é `@vestigo/core/area-agenda`, a mesma do painel do tutor.
   */
  encontros: EncontroDaAgenda[];
  chegaram: { total: number; rostos: { inicial: string; avatar: string | null }[] };
}

export interface EncontroDaAgenda {
  id: string;
  titulo: string;
  /** ISO. A tela converte para o fuso do APARELHO, com a sigla colada. */
  comecaEm: string;
  duracaoMinutos: number;
}

export interface PerfilDoAluno {
  tema: TemaDaArea;
  perfil: {
    nome: string | null;
    handle: string | null;
    bio: string | null;
    avatar: string | null;
  };
  avatar: { teto: number; formatos: readonly string[]; frase: string };
}

export interface ComentarioDoMural {
  id: string;
  body: string;
  authorKind: 'member' | 'operator';
  authorMemberId: string | null;
  autor: string;
  /** Marcado pelo autor do POST. É o único fato da comunidade que dá ponto. */
  helpfulAt: string | null;
  removedAt: string | null;
  createdAt: string;
  /** A marca de editado da resposta (`Q8`). Só o autor a produz. */
  editedAt: string | null;
  removido: boolean;
  souEu: boolean;
  /** O rosto também na resposta: saber quem respondeu decide se a pessoa lê. */
  rosto: RostoNaLinha;
  /**
   * ── OS CURTIRES DESTA RESPOSTA (`CO12`, 0363) ────────────────────────────
   *
   * `count(*)` feito na MESMA consulta que decide o que este aluno vê, e nunca
   * uma coluna materializada — é isso que impede o placar de se mover por causa
   * invisível, que era a objecção do `agentes-juiz` na 0338.
   *
   * Opcionais porque um servidor mais velho não os manda, e a tela tem de
   * continuar a desenhar a conversa sem eles: um coração é um acessório da
   * resposta, e nunca a resposta.
   */
  votos?: number;
  votei?: boolean;
}

/**
 * ── A CAIXA «PARA PARTILHAR» (`Z2`) ─────────────────────────────────────────
 *
 * A oportunidade guardada, e nunca o ato. Uma conquista com `post_id` nulo é
 * uma pessoa que conquistou e ainda não contou — e pode nunca contar. **Fechar
 * a caixa não publica nada; nunca abrir a comunidade não publica nada.**
 */
export type ConquistaGuardada = ConquistaDoAluno;

export interface CaixaDeConquistas {
  conquistas: ConquistaGuardada[];
}

export interface PostAberto {
  tema: TemaDaArea;
  /** Os dois portoes da area (`Q9`) — a caixa de responder le o segundo. */
  portao: PortaoDaComunidade;
  post: PostDoMural & { votei: boolean; votos: number; votantes: number };
  comentarios: ComentarioDoMural[];
  /** Só o autor da pergunta marca a resposta que resolveu. */
  souOAutor: boolean;
}
